import { Vector2, Vector3, Plane, BoxGeometry, MeshBasicMaterial, Mesh, Frustum, Matrix4 } from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { computeProjectiveMatrix3d } from '../../plugins/3d-2d-transform/src/projectiveMatrix.js';
import { VapiChair } from '../vapi-UI/VapiChair.js';
import Vapi from '@vapi-ai/web';
import { releasePreloadedVapiWebRtc } from '../../helpers/preloadVapiWebRtc.js';
import { createVapiRCall } from '../../helpers/vapiRCall.js';
import { ReactiveComponent } from '../../core/ReactiveComponent.core.js';
import { ComponentRegistry } from '../../constants/componentRegistry.js';
import {
    getAiEventsFromConfig,
    buildVapiAiEventAssistantOverrides,
    mergeVapiAssistantOverrides,
    buildVapiStoreSessionDataOverrides,
    buildVapiCloseElementOverrides,
    buildVapiPromptVariableOverrides,
    buildVapiRoomPromptSystemContent,
    getVapiRoomPromptFromConfig,
    buildVapiTokenPromptSystemContent,
    buildVapiGuestUserOverrides,
    buildVapiGuestUserSystemContent,
    isCurrentUserGuest,
    STORE_SESSION_DATA_TOOL_NAME,
    CLOSE_ELEMENT_TOOL_NAME,
} from '../../constants/eventActions.js';
import { getAiTokenFromSearch } from '../../helpers/aiToken.js';
import { buildVapiAnalysisPlan } from '../../helpers/baglessMemory.js';
import {
    getAmplitudeSessionId,
    slugifyFieldKey,
    parseToolArguments,
    saveSessionInputData,
    INPUT_VALIDATION,
} from '../Tracking/sessionInputStore.js';
import { CONFIRM_BUTTON_SIZE, CONFIRM_NOTIFICATION_THEME } from '../Modals/Notifications/ConfirmNotification';
import { SCENE_INTERACTION } from '../../constants/scene.js';
import { requestAiInteractionConsent } from './helpers/aiInteractionConsent';

/**
 * VRVapiChairWithCSS3D - VAPI voice AI agent chair.
 * Uses CSS3DObject in scene (same level as VapiChairTest) with an invisible 3D anchor mesh.
 * Drag updates anchor position; perspective is applied on the chair DOM content.
 */
class VRVapiChairWithCSS3D extends ReactiveComponent {
    /** Max longer side when fitting the chair root to the image (matches FileDragDropUploadController pre-upload cap). */
    static MAX_AGENT_IMAGE_DIMENSION_PX = 1024;
    static LEGACY_SIZE_KEYS = ['chairIndicatorSize'];
    static BASE_WIDTH_PX = 480;

    static sanitizeLegacySizeConfig(config = {}) {
        if (!config || typeof config !== 'object') return {};
        const sanitized = { ...config };
        VRVapiChairWithCSS3D.LEGACY_SIZE_KEYS.forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(sanitized, key)) {
                delete sanitized[key];
            }
        });
        return sanitized;
    }

    static _vapiErrorText(value) {
        return typeof value === 'string' && value.trim() ? value.trim() : '';
    }

    static formatVapiError(err) {
        if (err == null) return '';
        const direct = VRVapiChairWithCSS3D._vapiErrorText(err);
        if (direct) return direct;
        if (typeof err !== 'object') return '';
        const nodes = [err, err.error, err.error?.error, err.message];
        for (const node of nodes) {
            if (!node || typeof node !== 'object') continue;
            const text = VRVapiChairWithCSS3D._vapiErrorText(node.message)
                || VRVapiChairWithCSS3D._vapiErrorText(node.errorMsg)
                || VRVapiChairWithCSS3D._vapiErrorText(node.msg)
                || VRVapiChairWithCSS3D._vapiErrorText(node.error);
            if (text) return text;
        }
        return '';
    }

    /** Daily emits this when VAPI ends the meeting. It is a normal hang-up, not a failure. */
    static isNormalCallEnd(err) {
        if (VRVapiChairWithCSS3D._isMeetingEndedText(err)) return true;
        if (!err || typeof err !== 'object') return false;
        const nodes = [err, err.error, err.message, err.error?.error, err.error?.message];
        return nodes.some((node) => {
            if (VRVapiChairWithCSS3D._isMeetingEndedText(node)) return true;
            if (!node || typeof node !== 'object') return false;
            if (node.type === 'ejected') return true;
            const msg = VRVapiChairWithCSS3D._vapiErrorText(node.msg)
                || VRVapiChairWithCSS3D._vapiErrorText(node.errorMsg)
                || VRVapiChairWithCSS3D._vapiErrorText(node.message)
                || VRVapiChairWithCSS3D._vapiErrorText(node.error);
            return VRVapiChairWithCSS3D._isMeetingEndedText(msg);
        });
    }

    static _isMeetingEndedText(value) {
        const msg = VRVapiChairWithCSS3D._vapiErrorText(value);
        return /meeting has ended|ended due to ejection|\bVM2600\b/i.test(msg);
    }

    constructor(params = {}) {
        super(null, {
            mode: params.editMode ? 'edit' : 'view'
        });
        const { model, camera, controls, raycaster, position, id, dbConfig, sizes, width, height } = params;
        this._initWidth = width ?? null;
        this._initHeight = height ?? null;
        this.id = id;
        this.model = model;
        this.camera = camera;
        this.controls = controls || null;
        this.position = position || { x: 0, y: 0, z: -500 };
        this.dbConfig = VRVapiChairWithCSS3D.sanitizeLegacySizeConfig(dbConfig || {});
        this._raycaster = raycaster || null;
        this._sizes = sizes || { width: window.innerWidth, height: window.innerHeight };
        this._viewportEl = params.viewportElement || document.querySelector('#webgl') || document.querySelector('#css3d');
        this._ndc = new Vector2();
        this._dragPlane = new Plane();
        this._dragPlaneNormal = new Vector3();
        this._planeIntersectPoint = new Vector3();
        this._dragGrabOffset = new Vector3();
        this._tmpVec = new Vector3();
        this._cameraFrustum = new Frustum();
        this._viewProjectionMatrix = new Matrix4();
        this._dragStartDistance = 0;
        this._dragOffsetX = 0;
        this._dragOffsetY = 0;
        this._isChairHovered = false;
        this.isDragging = false;
        this._captureEl = null;
        this._isEditMode = !!params.editMode;
        this._css3dScale = 1;
        this._userScale = Number(this.dbConfig?.size ?? this.dbConfig?.scale) || 1;
        // Keep compatibility with ModeEdit resize/rotate controls that check objConfig on selected objects.
        this.objConfig = {
            isResizable: true,
            isRotatable: true,
        };
        this._rootTopOffset = '0px';
        this._dragFixedDistance = null;
        this._dragDirOffset = new Vector3(); // object dir minus cursor dir at grab point
        this._resetPerspectiveOnNextImageFit = false;
        this._preservePerspectiveOnNextImageUpdate = false;
        this.parent = this;
        this.name = 'vraiAgent';
        this.type = 'vraiAgent';

        this._buildNode();
        this.anchor.position.set(
            this.position.x,
            this.position.y,
            this.position.z !== undefined ? this.position.z : -500
        );
        // In edit mode this component itself can be selected; expose anchor position for generic sliders/wheel handlers.
        this.position = this.anchor.position;
        this.anchor.userData.vraiAgentChair = this;

        if (this.state.mode === 'edit') {
            this._attachDragListeners();
        }
        this._setupVapiClickHandler();
        this._setupCallControls();

        this._vapi = null;
        this._vapiCallActive = false;
        this._vapiConnecting = false;
        this._vapiAssistantSpeaking = false;
        this._vapiCueState = 'idle';
        this._vapiCues = null;
        this._vapiConsentPending = false;
        this._vapiSessionVersion = 0;
        this._stoppedVapiInstances = new WeakSet();
        this._webcamStream = null;
        this._timerInterval = null;
        this._callStartTime = null;
        this._lastCallDurationSec = null;
        this._lastCallStartedAt = null;
        this._lastCallEndedAt = null;
        this._isMicMuted = false;
        this._isCameraHidden = false;
        this._callTranscriptHistory = [];
        this._vapiCallId = null;
        this._vapiLocalId = null;
        this._vapiReportToken = null;
        this._vapiSaveTranscriptUrl = null;
        this._vapiWebhookUrl = null;
        this._vapiConversationSnapshot = null;
        this._callReportRetryTimer = null;
        this._agentSceneChangeReported = false;
        this._applyVisibilityFromConfig();
        return this;
    }

    _setupCallControls() {
        const timerEl = this.dom?.querySelector('.vrai-timer');
        const statusEl = this.dom?.querySelector('.vrai-status-agent');
        const callControlsEl = this.dom?.querySelector('.vrai-agent-call-controls');
        const chairsEl = this.dom?.querySelector('.vrai-agent-chairs');
        const chairEl = this.dom?.querySelector('.vrai-agent-chair');
        const agentId = this._getAgentId();
        if (!agentId || !this.model) return;

        const formatTime = (s) => {
            const m = Math.floor(s / 60), sec = s % 60;
            return String(m).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
        };

        const setStatus = (text, className = '') => {
            if (!statusEl) return;
            statusEl.textContent = text || '';
            statusEl.className = 'status-side status-agent vrai-status-agent' + (className ? ` ${className}` : '');
        };

        const startTimer = () => {
            this._callStartTime = Date.now();
            this._lastCallStartedAt = this._callStartTime;
            this._lastCallEndedAt = null;
            if (this._timerInterval) clearInterval(this._timerInterval);
            if (timerEl) timerEl.textContent = '00:00';
            this._timerInterval = setInterval(() => {
                if (timerEl && this._callStartTime) {
                    timerEl.textContent = formatTime(Math.floor((Date.now() - this._callStartTime) / 1000));
                }
            }, 1000);
        };

        const stopTimer = () => {
            if (this._timerInterval) {
                clearInterval(this._timerInterval);
                this._timerInterval = null;
            }
            this._callStartTime = null;
            if (timerEl) timerEl.textContent = '00:00';
        };

        const showCallView = () => {
            if (callControlsEl) callControlsEl.classList.remove('hidden');
            if (chairEl) chairEl.classList.add('in-call');
        };

        const showGridView = () => {
            if (callControlsEl) callControlsEl.classList.add('hidden');
            if (chairEl) chairEl.classList.remove('in-call');
            setStatus('');
            stopTimer();
        };

        this._handleCallStart = (e) => {
            if (String(e?.data?.vapiAgentId) !== String(agentId)) return;
            this._lastCallDurationSec = null;
            this._callTranscriptHistory = [];
            this._vapiConversationSnapshot = null;
            this._clearCallReportRetry();
            // Do not force camera on here — user may have turned video off before/during connect.
            showCallView();
            startTimer();
            setStatus(this._getAgentDisplayName());
            // Web calls are listening at call-start. Websocket calls keep the waiting loop
            // until a later in-progress / agent status.
            if (e?.data?.listening) {
                this._playVapiConnectedCue();
            }
        };

        this._handleCallEnd = (e) => {
            if (String(e?.data?.vapiAgentId) !== String(agentId)) return;
            this._playVapiEndedCue();
            this._lastCallEndedAt = Date.now();
            if (this._callStartTime) {
                this._lastCallDurationSec = Math.floor((Date.now() - this._callStartTime) / 1000);
            } else {
                this._lastCallDurationSec = 0;
            }
            showGridView();
            this._maybeShowTalkSummaryModal(e?.data || {});
        };

        this._handleStatus = (e) => {
            if (String(e?.data?.vapiAgentId) !== String(agentId)) return;
            const state = e?.data?.state || '';
            const msg = e?.data?.message
                || VRVapiChairWithCSS3D.formatVapiError(e?.data?.error)
                || '';
            if (state === 'request-media' || state === 'connecting') {
                this._startVapiWaitingCue();
                showCallView();
                setStatus(state === 'request-media'
                    ? this._mediaRequestStatus()
                    : ((typeof $trans !== 'undefined' && $trans('vrai-agent-connecting')) || 'Connecting...'), 'calling-agent');
            } else if (state === 'connect-failed') {
                this._playVapiEndedCue();
                showGridView();
                setStatus(msg || ((typeof $trans !== 'undefined' && $trans('vrai-agent-connect-failed')) || 'Connection failed'));
            } else if (state === 'no-assistant') {
                this._playVapiEndedCue();
                showGridView();
                setStatus(msg || ((typeof $trans !== 'undefined' && $trans('vrai-agent-no-assistant')) || 'Agent not configured'));
            } else if (state === 'media-failed') {
                this._playVapiEndedCue();
                showGridView();
                setStatus(msg || this._mediaFailedStatus());
            }
            // Avoid putting "Agent: Thinking/Talking" on top of the chair image; the transcription window
            // is a better place for these ephemeral statuses (handled by transcription component).
            else if (state === 'agent-talking' || state === 'agent-thinking' || state === 'agent') {
                if (this._vapiCueState === 'waiting') this._playVapiConnectedCue();
                if (state === 'agent') setStatus(this._getAgentDisplayName(), 'calling-agent');
                else setStatus('', 'calling-agent');
            }
        };

        this._handleVapiError = (e) => {
            if (String(e?.data?.vapiAgentId) !== String(agentId)) return;
            if (VRVapiChairWithCSS3D.isNormalCallEnd(e?.data?.error)) {
                showGridView();
                return;
            }
            this._playVapiEndedCue();
            const msg = VRVapiChairWithCSS3D.formatVapiError(e?.data?.error)
                || ((typeof $trans !== 'undefined' && $trans('vrai-agent-connect-failed')) || 'Connection failed');
            showGridView();
            setStatus(msg);
        };

        this.model.addEventListener('vapi-agent-call-start', this._handleCallStart);
        this.model.addEventListener('vapi-agent-call-end', this._handleCallEnd);
        this.model.addEventListener('vapi-agent-status', this._handleStatus);
        this.model.addEventListener('vapi-agent-error', this._handleVapiError);

        this._handleSendText = (e) => {
            if (String(e?.data?.vapiAgentId) !== String(agentId)) return;
            const text = (e?.data?.text || '').trim();
            if (!text || !this._vapi || !this._vapiCallActive) return;
            // Commands and programmatic sends go straight to VAPI (no "Send message" prompt).
            this._sendUserTranscriptMessage(text);
        };
        this.model.addEventListener('vapi-agent-send-text', this._handleSendText);

        this._handleEndVapiCallRequest = (e) => {
            const rid = e?.data?.vapiAgentId;
            if (rid != null && String(rid) !== String(agentId)) return;
            if (rid == null) {
                const active = this.model?.getActiveVapiChair?.();
                if (active !== this) return;
            }
            if (!this._vapiCallActive && !this._vapiConnecting) return;
            this._endCall();
        };
        this.model.addEventListener(SCENE_INTERACTION.END_VAPI_CALL, this._handleEndVapiCallRequest);

        /**
         * Start/end overlays share the same screen position. Record which control received
         * pointerdown and handle on click so a start tap cannot retarget to the end button.
         * Do not preventDefault on pointerdown — it suppresses click on the start <div>.
         * Defer clearing the gesture until after click (pointerup runs before click).
         */
        this._chairOverlayGesture = null;
        this._onChairOverlayPointerDown = (e) => {
            if (e.button !== 0) return;
            if (this.state.mode === 'edit') return;
            e.stopPropagation();
            const target = e.currentTarget;
            if (target === this._chairIndicatorEl) {
                this._chairOverlayGesture = 'start';
            } else if (target === this._chairEndIndicatorEl) {
                this._chairOverlayGesture = 'end';
            } else {
                return;
            }
            const clearGesture = () => {
                window.setTimeout(() => { this._chairOverlayGesture = null; }, 0);
                window.removeEventListener('pointerup', clearGesture);
                window.removeEventListener('pointercancel', clearGesture);
            };
            window.addEventListener('pointerup', clearGesture, { once: true });
            window.addEventListener('pointercancel', clearGesture, { once: true });
        };
        this._onChairStartClick = (e) => {
            if (this.state.mode === 'edit') return;
            e.stopPropagation();
            e.preventDefault();
            if (this._chairOverlayGesture !== 'start') return;
            this._chairOverlayGesture = null;
            if (this._vapiCallActive || this._vapiConnecting || this._vapiConsentPending) return;
            this._runConfiguredClickActionOrStartCall();
        };
        this._onChairEndClick = (e) => {
            if (this.state.mode === 'edit') return;
            e.stopPropagation();
            e.preventDefault();
            if (this._chairOverlayGesture !== 'end') return;
            this._chairOverlayGesture = null;
            if (!this._vapiCallActive && !this._vapiConnecting) return;
            this._endCall();
        };
        this._chairIndicatorEl?.addEventListener('pointerdown', this._onChairOverlayPointerDown);
        this._chairIndicatorEl?.addEventListener('click', this._onChairStartClick);
        this._chairEndIndicatorEl?.addEventListener('pointerdown', this._onChairOverlayPointerDown);
        this._chairEndIndicatorEl?.addEventListener('click', this._onChairEndClick);
        this._onPageHidePersistCallReport = () => {
            if (!this._vapiCallActive && !this._callTranscriptHistory?.length && !this._vapiCallId) return;
            this._persistCallReport({ cancelled: true, pageHide: true }, { keepalive: true });
        };
        if (typeof window !== 'undefined') {
            window.addEventListener('pagehide', this._onPageHidePersistCallReport);
        }
    }

    _sendUserTranscriptMessage(text) {
        const t = (text || '').trim();
        if (!t || !this._vapi || !this._vapiCallActive) return;
        this._vapi.send({
            type: 'add-message',
            message: { role: 'user', content: t },
            triggerResponseEnabled: true,
        });
        this._addCallTranscript('user', t, true, { source: 'command', forceNewEntry: true });
        this._dispatchVapiEvent('vapi-agent-command', {
            command: t,
            transcript: t,
            source: 'command',
        });
        this._dispatchVapiEvent('vapi-agent-transcript', {
            role: 'user',
            transcript: t,
            isFinal: true,
            sentAt: Date.now(),
            source: 'command',
        });
    }

    _updateCallRunningClass() {
        const chairEl = this.dom?.querySelector('.vrai-agent-chair');
        if (!chairEl) return;
        chairEl.classList.toggle('vrai-agent-call-running', !!(this._vapiConnecting || this._vapiCallActive));
    }

    hasActiveVapiSession() {
        return !!(this._vapi || this._vapiCallActive || this._vapiConnecting);
    }

    _isCurrentVapiSession(sessionVersion, vapi = null) {
        if (this._vapiSessionVersion !== sessionVersion) return false;
        if (vapi && this._vapi !== vapi) return false;
        return true;
    }

    _stopDetachedVapiInstance(vapi) {
        if (!vapi || this._stoppedVapiInstances.has(vapi)) return;
        this._stoppedVapiInstances.add(vapi);
        try {
            vapi.stop();
        } catch (e) {
            console.warn('[VAPI] stale instance stop failed', e);
        }
    }

    _isStaleVapiInstance(vapi, sessionVersion) {
        return !this._isCurrentVapiSession(sessionVersion, vapi);
    }

    _forceStopVapiSession({
        dispatchCallEnd = true,
        callEndData = { cancelled: true },
        stopSdk = true
    } = {}) {
        const vapi = this._vapi;
        const hadSession = this.hasActiveVapiSession();
        const shouldPersist = this._vapiCallActive
            || !!this._vapiCallId
            || !!(this._callTranscriptHistory?.length)
            || !!(this._vapiConversationSnapshot?.length);
        if (shouldPersist) {
            this._lastCallEndedAt = this._lastCallEndedAt || Date.now();
            if (this._lastCallDurationSec == null && this._callStartTime) {
                this._lastCallDurationSec = Math.floor((Date.now() - this._callStartTime) / 1000);
            }
            this._persistCallReport(callEndData, {
                keepalive: !!(callEndData?.pageHide || (callEndData?.sceneChange && !callEndData?.agentSceneChange)),
            });
        }
        this._vapiSessionVersion += 1;

        // Clear local state first so stale VAPI callbacks from the previous scene are ignored.
        this._vapi = null;
        this._vapiTransport = null;
        this._vapiCallActive = false;
        this._vapiConnecting = false;
        this._vapiAssistantSpeaking = false;

        if (stopSdk && vapi) {
            try {
                vapi.stop();
            } catch (e) {
                console.warn('[VAPI] force stop failed', e);
            }
        }

        this._stopMedia();
        this._updateCallRunningClass();

        if (dispatchCallEnd && hadSession) {
            this._dispatchVapiEvent('vapi-agent-call-end', callEndData);
        }

        return hadSession;
    }

    stopForSceneChange() {
        if (this._agentSceneChangeReported) return false;
        if (this.model && !this.model._vapiAgentSceneChangeHandoff) {
            this.model._vapiHandoffConversations = null;
        }
        return this._forceStopVapiSession({
            callEndData: { cancelled: true, sceneChange: true }
        });
    }

    _cancelPendingConnect() {
        if (!this._vapiConnecting) return;
        this._forceStopVapiSession({
            callEndData: { cancelled: true }
        });
    }

    // Kept for external controls (e.g. leave-vapi-call in bottom nav).
    _endCall() {
        const msg = (typeof $trans !== 'undefined' && $trans('vrai-agent-end-call-confirm')) || 'End call?';
        const confirmOpts = {
            confirmBtnTxt: (typeof $trans !== 'undefined' && $trans('action-end-call')) || 'End call',
            cancelBtnTxt: (typeof $trans !== 'undefined' && $trans('cancel-text')) || 'Cancel',
        };

        if (this._vapiConnecting && !this._vapiCallActive) {
            if (this.model?.confirm) {
                this.model.confirm(msg, () => this._cancelPendingConnect(), confirmOpts);
            } else {
                this._cancelPendingConnect();
            }
            return;
        }
        if (!this._vapiCallActive || !this._vapi) return;
        const doStop = () => {
            this._forceStopVapiSession({
                callEndData: { cancelled: true, userEnded: true }
            });
        };
        if (this.model?.confirm) this.model.confirm(msg, doStop, confirmOpts);
        else doStop();
    }

    _normalizeTranscriptText(text) {
        return String(text || '')
            .toLowerCase()
            .replace(/[\W_]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    _mergeSubtitleText(prev, next) {
        if (!prev) return next;
        const prevTrimmed = prev.replace(/[\s.,;:!?]+$/, '');
        if (next.startsWith(prev) || (prevTrimmed && next.startsWith(prevTrimmed))) return next;
        const prefixLen = Math.min(prev.length, next.length);
        let commonPrefix = 0;
        for (let i = 0; i < prefixLen && prev[i] === next[i]; i++) commonPrefix++;
        if (commonPrefix >= 6 && next.length >= commonPrefix) return next;
        let overlap = Math.min(prev.length, next.length);
        while (overlap > 0) {
            if (prev.endsWith(next.slice(0, overlap))) return prev + next.slice(overlap);
            overlap--;
        }
        return prev + (/[\s.:,;!?]$/.test(prev) ? '' : ' ') + next;
    }

    _reconcileSubtitleText(prev, next) {
        if (!prev) return next;
        const normalizedPrev = this._normalizeTranscriptText(prev);
        const normalizedNext = this._normalizeTranscriptText(next);
        if (!normalizedNext) return prev;
        if (!normalizedPrev) return next;
        if (
            normalizedNext === normalizedPrev ||
            normalizedNext.startsWith(normalizedPrev) ||
            normalizedPrev.startsWith(normalizedNext)
        ) {
            return next;
        }
        return this._mergeSubtitleText(prev, next);
    }

    _addCallTranscript(role, text, isFinal = false, opts = {}) {
        const transcript = (text || '').trim();
        if (!transcript) return;
        const normalizedRole = role === 'user' ? 'user' : 'assistant';
        const source = opts?.source || null;
        const forceNewEntry = !!(opts?.forceNewEntry || source === 'command');
        const last = this._callTranscriptHistory[this._callTranscriptHistory.length - 1];
        if (!forceNewEntry && last && last.role === normalizedRole) {
            if (last.isFinal) {
                if (this._normalizeTranscriptText(last.text) === this._normalizeTranscriptText(transcript)) return;
                this._callTranscriptHistory.push({ role: normalizedRole, text: transcript, isFinal: !!isFinal });
                return;
            }
            const reconciled = this._reconcileSubtitleText(last.text, transcript);
            if (reconciled !== last.text) last.text = reconciled;
            if (isFinal) last.isFinal = true;
            return;
        }
        this._callTranscriptHistory.push({ role: normalizedRole, text: transcript, isFinal: !!isFinal });
    }

    _buildTalkSummaryTitle() {
        const userName = this.model?.config?.userName || 'User';
        const agentName = this.dbConfig?.chairName || this._t('vrai-agent-text', 'Agent');
        const conversationLbl = this._t('vrai-agent-talk-summary-title', 'Conversation');
        const lang = localStorage.getItem('language') || 'en';
        const when = new Date().toLocaleString(lang);
        return `${conversationLbl}: ${userName} - ${agentName} (${when})`;
    }

    _formatCallDuration(seconds) {
        const s = Math.max(0, Math.floor(Number(seconds) || 0));
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return String(m).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
    }

    _sanitizeFilenameSegment(str, fallback) {
        let s = String(str || '').trim()
            .replace(/[\/\\:*?"<>|\u0000-\u001f]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 48);
        return s || fallback;
    }

    _buildTalkSummaryDownloadFilename() {
        const userName = this.model?.config?.userName || 'User';
        const agentName = this.dbConfig?.chairName || ((typeof $trans !== 'undefined' && $trans('vrai-agent-text')) || 'Agent');
        const userSlug = this._sanitizeFilenameSegment(userName, 'user');
        const agentSlug = this._sanitizeFilenameSegment(agentName, 'agent');
        const dateSlug = new Date().toISOString().slice(0, 10);
        return `${userSlug}-${agentSlug}-${dateSlug}.txt`;
    }

    _buildTalkSummaryContent(history = this._callTranscriptHistory) {
        if (!Array.isArray(history) || history.length === 0) {
            return '';
        }
        const userName = (this.model?.config?.userName || this.model?.config?.name || '').trim() || '-';
        const scenarioName = (this.dbConfig?.scenarioName || this.dbConfig?.chairName || this.dbConfig?.name || '').trim() || '-';
        const startTs = this._lastCallStartedAt || Date.now();
        const endTs = this._lastCallEndedAt || Date.now();
        const dateText = this._formatReportDate(startTs);
        const timeText = this._formatReportClock(startTs);
        const durationText = this._formatCallDuration(this._lastCallDurationSec);
        const brandLine = this._t('vrai-agent-talk-summary-report-brand', 'myLINA - Integration smart gedacht');
        const versionLine = this._t('vrai-agent-talk-summary-report-version', 'myLINA Version: MVP2');
        const scenarioLabel = this._t('vrai-agent-talk-summary-scenario-label', 'Dialog Szenario');
        const participantLabel = this._t('vrai-agent-talk-summary-participant-label', 'Proband');
        const dateLabel = this._t('date-text', 'Datum');
        const timeLabel = this._t('time-text', 'Uhrzeit');
        const durationLabel = this._t('duration-text', 'Dauer');
        const transcriptTitle = this._t('vrai-agent-talk-summary-transcript-title', 'DIALOG VERLAUF');
        const lines = this._groupTranscriptDownloadEntries(history)
            .map(({ role, lines: groupedLines }) => this._formatTranscriptDownloadEntry(role, groupedLines))
            .filter(Boolean)
            .join('\n\n');
        return [
            '#############################################################################################',
            '##                                                                                         ##',
            this._buildReportLine(brandLine),
            '##                                                                                         ##',
            this._buildReportLine(versionLine),
            this._buildReportLine(`${scenarioLabel}: ${scenarioName}`),
            this._buildReportLine(`${participantLabel}: ${userName}`),
            this._buildReportLine(`${dateLabel}: ${dateText}`),
            this._buildReportLine(`${timeLabel}: ${timeText}`),
            this._buildReportLine(`${durationLabel}: ${durationText}`),
            '##                                                                                         ##',
            '#############################################################################################',
            '',
            `################################## ${transcriptTitle} ##########################################`,
            '',
            lines
        ].join('\n');
    }

    _formatTranscriptDownloadEntry(role, lines) {
        if (!Array.isArray(lines) || lines.length === 0) return '';
        const label = role === 'user'
            ? this._t('vrai-agent-talk-summary-role-user', '[PROBAND]: ')
            : this._t('vrai-agent-talk-summary-role-agent', '[PATIENT ] : ');
        return [label + lines[0], ...lines.slice(1)].join('\n');
    }

    _groupTranscriptDownloadEntries(history) {
        const grouped = [];
        for (const { role, text } of (history || [])) {
            const raw = String(text || '').trim();
            if (!raw) continue;
            const chunks = raw
                .split(/\r?\n+/)
                .map(line => line.trim())
                .filter(Boolean);
            if (!chunks.length) continue;
            const last = grouped[grouped.length - 1];
            if (last && last.role === role) {
                last.lines.push(...chunks);
            } else {
                grouped.push({ role, lines: [...chunks] });
            }
        }
        return grouped;
    }

    _buildTalkSummaryDownloadText(nameOfTalk, content) {
        return `${content || ''}\n`;
    }

    _buildReportLine(text) {
        const content = `##   ${String(text || '').trim()}`;
        return content.padEnd(91, ' ') + '##';
    }

    _formatReportDate(ts) {
        const d = new Date(ts || Date.now());
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
    }

    _formatReportClock(ts) {
        const d = new Date(ts || Date.now());
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${hh}:${mm}:${ss}`;
    }

    _downloadTalkSummaryText(nameOfTalk, content) {
        const text = this._buildTalkSummaryDownloadText(nameOfTalk, content);
        if (!text?.trim()) return;
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = this._buildTalkSummaryDownloadFilename();
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    }

    _escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text == null ? '' : String(text);
        return div.innerHTML;
    }

    _t(key, fallback) {
        if (typeof $trans !== 'function') return fallback || key;
        const value = $trans(key, {}, fallback || key);
        if (!value || value === key) return fallback || key;
        return value;
    }

    _snapshotTalkSummary() {
        this._hydrateCallTranscriptHistoryFromTranscription();
        if (!this._callTranscriptHistory?.length) return null;
        return {
            history: this._callTranscriptHistory.map((entry) => ({ ...entry })),
            durationSec: this._lastCallDurationSec,
        };
    }

    _showTalkSummaryModal(snapshot = null) {
        const history = snapshot?.history ?? this._callTranscriptHistory;
        const durationSec = snapshot?.durationSec ?? this._lastCallDurationSec;
        const nameOfTalk = this._buildTalkSummaryTitle();
        const content = this._buildTalkSummaryContent(history);
        const closeTxt = this._t('close-text', 'Close');
        const downloadTxt = this._t('download-text', 'Download');
        const titleLbl = this._t('vrai-agent-talk-summary-title', 'Conversation');
        const contentLbl = this._t('content-text', 'Content');
        const durationLbl = this._t('duration-text', 'Duration');
        const nameAndDuration = `${nameOfTalk}\n${durationLbl}: ${this._formatCallDuration(durationSec)}`;
        const message = `
            <div class="vrai-talk-summary-confirm" style="display:flex;flex-direction:column;gap:12px;align-items:stretch;width:min(90vw,760px);max-width:100%;margin:2px auto 0;">
                <div style="display:flex;flex-direction:column;gap:6px;padding:10px 12px;border-radius:10px;border:1px solid rgba(255,255,255,0.14);background:linear-gradient(180deg,#202736 0%,#1a1f2c 100%);">
                    <div style="font-size:12px;letter-spacing:.03em;text-transform:uppercase;font-weight:700;color:#9fb4d9;">${this._escapeHtml(titleLbl)}</div>
                    <textarea readonly rows="3" style="width:100%;min-height:76px;resize:vertical;padding:10px 11px;border-radius:8px;border:1px solid rgba(166,188,225,0.35);background:#0e141f;color:#edf3ff;line-height:1.45;font-size:13px;font-family:inherit;outline:none;box-shadow:none;">${this._escapeHtml(nameAndDuration)}</textarea>
                </div>
                <div style="display:flex;flex-direction:column;gap:6px;">
                    <div style="font-size:12px;letter-spacing:.03em;text-transform:uppercase;font-weight:700;color:#9fb4d9;">${this._escapeHtml(contentLbl)}</div>
                    <textarea readonly rows="12" style="width:100%;min-height:320px;max-height:60vh;resize:vertical;padding:12px;border-radius:10px;border:1px solid rgba(255,255,255,0.16);background:#111827;color:#f5f7fb;line-height:1.55;font-size:14px;font-family:inherit;outline:none;box-shadow:inset 0 1px 0 rgba(255,255,255,0.03);">${this._escapeHtml(content)}</textarea>
                </div>
            </div>
        `;
        this.model?.confirm?.(message, () => {
            this._downloadTalkSummaryText(nameOfTalk, content);
        }, {
            header: true,
            title: titleLbl,
            confirmBtnTxt: downloadTxt,
            cancelBtnTxt: closeTxt,
            dimmerClose: false,
            position: 'top middle',
            modalTheme: CONFIRM_NOTIFICATION_THEME.DARK,
            buttonSize: CONFIRM_BUTTON_SIZE.DEFAULT,
        });
    }

    _hydrateCallTranscriptHistoryFromTranscription() {
        if (this._callTranscriptHistory?.length) return;
        const agentId = this._getAgentId();
        if (!agentId) return;
        const tr = (this.model?.view?.vraiAgentTranscriptions || []).find(
            (t) => String(t.dbConfig?.vapiAgentId ?? t._vapiAgentId ?? '') === String(agentId)
        );
        if (!tr) return;
        const entries = Array.isArray(tr._subtitleHistory) ? tr._subtitleHistory : [];
        entries.forEach((entry) => {
            if (entry?.text) {
                this._addCallTranscript(entry.role, entry.text, true, { forceNewEntry: true });
            }
        });
        if (tr._liveSubtitleEntry?.text) {
            this._addCallTranscript(
                tr._liveSubtitleEntry.role,
                tr._liveSubtitleEntry.text,
                true,
                { forceNewEntry: true }
            );
        }
    }

    _hydrateCallTranscriptHistoryFromConversation() {
        if (this._callTranscriptHistory?.length) return;
        const messages = this._vapiConversationSnapshot;
        if (!Array.isArray(messages)) return;
        messages.forEach((entry) => {
            const roleRaw = String(entry?.role || '').toLowerCase();
            const role = roleRaw === 'user' ? 'user' : ((roleRaw === 'bot' || roleRaw === 'assistant') ? 'assistant' : null);
            if (!role) return;
            const text = String(entry.message ?? entry.content ?? entry.transcript ?? '').trim();
            if (text) {
                this._addCallTranscript(role, text, true, { forceNewEntry: true, source: 'conversation-update' });
            }
        });
    }

    _storeConversationSnapshot(message) {
        const messages = message?.conversation
            || message?.messages
            || message?.artifact?.messages
            || null;
        if (Array.isArray(messages) && messages.length) {
            this._vapiConversationSnapshot = messages;
        }
    }

    _captureCallIdFromPayload(payload) {
        if (!payload || typeof payload !== 'object') return;
        const id = payload.call?.id
            || payload.callId
            || payload.metadata?.callId
            || payload.call?.callId;
        if (id && id !== 'unknown') this._vapiCallId = id;
    }

    _newCallLocalId() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return `local_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }

    _clearCallReportRetry() {
        if (this._callReportRetryTimer) {
            clearTimeout(this._callReportRetryTimer);
            this._callReportRetryTimer = null;
        }
    }

    /** Second save only when the hang-up summarize came back without a summary. */
    _scheduleCallReportRetry(callEndData) {
        this._clearCallReportRetry();
        this._callReportRetryTimer = setTimeout(() => {
            this._callReportRetryTimer = null;
            if (this._vapiCallActive || this._vapiConnecting) return;
            this._persistCallReport(callEndData, { isRetry: true });
        }, 12000);
    }

    /** In-flight hang-up summarize. The next call waits for it so that summary is on the new call. */
    _trackSummarizeRequest(request) {
        const settled = Promise.resolve(request).then(() => undefined, () => undefined);
        const prev = this.model?._vapiSummarizePromise || null;
        const combined = prev ? Promise.all([prev, settled]).then(() => undefined) : settled;
        if (!this.model) return combined;
        this.model._vapiSummarizePromise = combined;
        combined.finally(() => {
            if (this.model && this.model._vapiSummarizePromise === combined) {
                this.model._vapiSummarizePromise = null;
            }
        });
        return combined;
    }

    async _waitForPendingSummarize() {
        let pending = this.model?._vapiSummarizePromise || null;
        while (pending) {
            await pending;
            const next = this.model?._vapiSummarizePromise || null;
            pending = next && next !== pending ? next : null;
        }
    }

    _currentSceneId() {
        return ((this.model?.currentScene?.id ?? this.model?.currentSceneId ?? '') + '').trim();
    }

    _newVapiSessionString() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return `sess_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }

    _trackingVisitSessionString() {
        const fromTracking = this.model?.view?.tracking?.getSessionString?.()
            || this.model?.view?.tracking?._temporaryTrackingSessionString;
        if (fromTracking) return String(fromTracking);
        return getAmplitudeSessionId() || this._newVapiSessionString();
    }

    /**
     * One VAPI session per room. The first room keeps the visit id.
     * A later room gets a new id so its agent does not inherit the previous room's summary.
     */
    _getVisitSessionString() {
        const sceneId = this._currentSceneId();
        if (!this.model || !sceneId) {
            if (this.model?._vapiVisitSessionString) return this.model._vapiVisitSessionString;
            const value = this._trackingVisitSessionString();
            if (this.model && value) this.model._vapiVisitSessionString = value;
            return value;
        }
        if (!this.model._vapiRoomSessionStrings || typeof this.model._vapiRoomSessionStrings !== 'object') {
            this.model._vapiRoomSessionStrings = {};
        }
        const existing = this.model._vapiRoomSessionStrings[sceneId];
        if (existing) {
            this.model._vapiVisitSessionString = existing;
            return existing;
        }
        const used = new Set(Object.values(this.model._vapiRoomSessionStrings));
        const tracking = this._trackingVisitSessionString();
        const value = tracking && !used.has(tracking) ? tracking : this._newVapiSessionString();
        this.model._vapiRoomSessionStrings[sceneId] = value;
        this.model._vapiVisitSessionString = value;
        return value;
    }

    _clearStaleSessionConversation() {
        const sessionString = this._getVisitSessionString();
        const cached = this.model?._vapiSessionConversation;
        if (cached && cached.sessionString && cached.sessionString !== sessionString) {
            this.model._vapiSessionConversation = null;
            this._sessionConversations = [];
        }
    }

    _knownFactsFromMemory(memory) {
        const raw = memory?.user?.known_facts;
        const list = Array.isArray(raw) ? raw : (typeof raw === 'string' && raw.trim() ? [raw] : []);
        return list.map((fact) => String(fact || '').trim()).filter(Boolean);
    }

    _stashTextFromAnalysis(memory, fallbackSummary = '') {
        if (!memory || typeof memory !== 'object' || Array.isArray(memory)) {
            return String(fallbackSummary || '').trim();
        }
        const lines = [];
        const user = memory.user && typeof memory.user === 'object' ? memory.user : {};
        [
            ['name', 'Name'],
            ['organization', 'Organization'],
            ['role', 'Role'],
            ['language', 'Language'],
        ].forEach(([key, label]) => {
            const value = String(user[key] || '').trim();
            if (value) lines.push(`${label}: ${value}`);
        });
        const summary = String(memory.conversations?.summary || fallbackSummary || '').trim();
        if (summary) lines.push(summary);
        const topics = Array.isArray(memory.conversations?.topics) ? memory.conversations.topics.map((item) => String(item || '').trim()).filter(Boolean) : [];
        if (topics.length) lines.push(`Topics: ${topics.join(', ')}`);
        const ai = memory.ai_context && typeof memory.ai_context === 'object' ? memory.ai_context : {};
        const nextGoal = String(ai.next_goal || '').trim();
        if (nextGoal) lines.push(`Next goal: ${nextGoal}`);
        const important = Array.isArray(ai.important_facts) ? ai.important_facts.map((item) => String(item || '').trim()).filter(Boolean) : [];
        if (important.length) lines.push(`Important:\n- ${important.join('\n- ')}`);
        const avoid = Array.isArray(ai.avoid_reasking) ? ai.avoid_reasking.map((item) => String(item || '').trim()).filter(Boolean) : [];
        if (avoid.length) lines.push(`Already known, do not ask again:\n- ${avoid.join('\n- ')}`);
        const open = memory.open_items && typeof memory.open_items === 'object' ? memory.open_items : {};
        const questions = Array.isArray(open.questions) ? open.questions.map((item) => String(item || '').trim()).filter(Boolean) : [];
        if (questions.length) lines.push(`Open questions:\n- ${questions.join('\n- ')}`);
        const openTopics = Array.isArray(open.topics) ? open.topics.map((item) => String(item || '').trim()).filter(Boolean) : [];
        if (openTopics.length) lines.push(`Continue: ${openTopics.join(', ')}`);
        return lines.join('\n').trim() || String(fallbackSummary || '').trim();
    }

    _conversationEntry(summary, memory) {
        const knownFacts = this._knownFactsFromMemory(memory);
        const text = this._stashTextFromAnalysis(memory, summary);
        if (!text && !knownFacts.length) return null;
        return { summary: text, knownFacts };
    }

    _getSessionConversations() {
        this._clearStaleSessionConversation();
        const cached = this.model?._vapiSessionConversation?.conversations;
        if (Array.isArray(cached)) return cached;
        return Array.isArray(this._sessionConversations) ? this._sessionConversations : [];
    }

    _setSessionConversations(conversations) {
        const next = (Array.isArray(conversations) ? conversations : [])
            .map((entry) => this._conversationEntry(entry?.summary, {
                user: { known_facts: entry?.knownFacts || entry?.known_facts },
                conversations: { summary: entry?.summary },
            }))
            .filter(Boolean);
        this._sessionConversations = next;
        if (!this.model) return;
        this.model._vapiSessionConversation = {
            sessionString: this._getVisitSessionString(),
            conversations: next,
            updatedAt: Date.now(),
        };
    }

    /** Append this call's own summary and known facts. Does not rewrite earlier calls. */
    _rememberSessionConversation(summary, memory, sessionString = null) {
        const entry = this._conversationEntry(summary, memory);
        if (!entry) return;
        const current = this._getVisitSessionString();
        if (sessionString && current && sessionString !== current) return;
        const list = this._getSessionConversations().slice();
        const last = list[list.length - 1];
        const sameSummary = last && last.summary === entry.summary;
        const sameFacts = last && JSON.stringify(last.knownFacts || []) === JSON.stringify(entry.knownFacts);
        if (sameSummary && sameFacts) return;
        list.push(entry);
        this._setSessionConversations(list);
    }

    _rememberServerConversations(conversations) {
        if (!Array.isArray(conversations)) return;
        this._setSessionConversations(conversations.map((entry) => ({
            summary: entry?.summary,
            knownFacts: entry?.knownFacts || entry?.known_facts,
        })));
    }

    async _fetchSessionConversations(tokenUrl, body) {
        const res = await fetch(tokenUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        return Array.isArray(data?.sessionConversations) ? data.sessionConversations : null;
    }

    /**
     * Earlier calls in this room. Empty on the first call in the room.
     * Logged only when a summary is actually attached to the next call.
     */
    _getSessionExtraPrompt() {
        const conversations = this._getSessionConversations();
        const summaries = conversations
            .map((entry) => String(entry?.summary || '').trim())
            .filter(Boolean);
        const facts = conversations.flatMap((entry) => (
            Array.isArray(entry?.knownFacts) ? entry.knownFacts : []
        ));
        if (!summaries.length && !facts.length) return '';
        const parts = [
            'Background you already know about this user. Treat it as one summary of what is known so far. Answer from it directly.',
            'Do not mention previous conversations, call order, or that the user told you something earlier. Do not say "in the first conversation", "in the second conversation", or anything similar.',
        ];
        if (summaries.length) {
            parts.push(summaries.join('\n\n'));
        }
        if (facts.length) {
            parts.push(`Known facts:\n${facts.map((fact) => `- ${fact}`).join('\n')}`);
        }
        const prompt = parts.join('\n\n');
        if (!this._loggedSessionPromptForCall) {
            this._loggedSessionPromptForCall = true;
            console.log('[VAPI] previous conversations sent to new agent', {
                summaries,
                knownFacts: facts,
                prompt,
            });
        }
        return prompt;
    }

    /** AI-token prompt plus earlier conversations in this room. */
    _getVapiExtraPrompt() {
        return [this._aiTokenPrompt, this._getSessionExtraPrompt()].filter(Boolean).join('\n\n');
    }

    _endedReasonFromCallEnd(callEndData = {}) {
        if (callEndData?.pageHide) return 'page_hide';
        if (callEndData?.sceneChange) return 'scene_change';
        if (callEndData?.userEnded) return 'user_ended';
        if (callEndData?.failed) return 'failed';
        if (callEndData?.normal) return 'ended';
        if (callEndData?.cancelled) return 'cancelled';
        return 'ended';
    }

    _isSummarizeCallEnd(callEndData = {}) {
        return !!(callEndData?.normal || callEndData?.userEnded || callEndData?.agentSceneChange);
    }

    /**
     * Agent-started scene changes must finish summarization before navigation.
     * The next room can auto-start another agent, and that agent needs this summary.
     */
    async _summarizeForAgentSceneChange() {
        const hasCall = this.hasActiveVapiSession()
            || !!this._vapiCallId
            || !!(this._callTranscriptHistory?.length)
            || !!(this._vapiConversationSnapshot?.length);
        if (this.model) this.model._vapiAgentSceneChangeHandoff = true;
        if (!hasCall || this._agentSceneChangeReported) {
            await this._waitForPendingSummarize();
            return;
        }
        this._agentSceneChangeReported = true;
        console.log('[VAPI] scene change waiting for summary');
        this._forceStopVapiSession({
            callEndData: { cancelled: true, sceneChange: true, agentSceneChange: true },
        });
        await this._waitForPendingSummarize();
    }

    _stashSummaryHandoff() {
        const conversations = this._getSessionConversations();
        if (!this.model || !conversations.length) return;
        this.model._vapiHandoffConversations = conversations.map((entry) => ({
            summary: entry.summary,
            knownFacts: Array.isArray(entry.knownFacts) ? entry.knownFacts.slice() : [],
        }));
        console.log('[VAPI] summary ready for next room', {
            conversations: this.model._vapiHandoffConversations.length,
        });
    }

    /** Previous room's summary, kept for the agent that auto-starts after an agent scene change. */
    _applySummaryHandoff() {
        const handoff = this.model?._vapiHandoffConversations;
        if (!Array.isArray(handoff) || !handoff.length) return;
        const current = this._getSessionConversations().slice();
        handoff.forEach((entry) => {
            const normalized = this._conversationEntry(entry?.summary, {
                user: { known_facts: entry?.knownFacts || entry?.known_facts },
                conversations: { summary: entry?.summary },
            });
            if (!normalized) return;
            const duplicate = current.some((item) => (
                item.summary === normalized.summary
                && JSON.stringify(item.knownFacts || []) === JSON.stringify(normalized.knownFacts || [])
            ));
            if (!duplicate) current.push(normalized);
        });
        this._setSessionConversations(current);
    }

    _buildCallReportOverrides() {
        const worldId = ((this.model?.config?.worldId ?? '') + '').trim();
        const sceneId = ((this.model?.currentScene?.id ?? this.model?.currentSceneId ?? '') + '').trim();
        const metadata = {
            baglessWorldId: worldId,
            baglessSceneId: sceneId,
            baglessAgentId: this._getAgentId(),
            baglessLocalId: this._vapiLocalId,
            baglessSessionString: this._getVisitSessionString(),
        };
        const aiToken = this._aiTokenValue || getAiTokenFromSearch(this.model?.urlParams);
        if (aiToken) metadata.baglessAiToken = aiToken;
        const overrides = mergeVapiAssistantOverrides(
            { metadata },
            {
                analysisPlan: buildVapiAnalysisPlan({
                    occurredAt: new Date().toISOString(),
                    spaceContext: this._getSpaceContext(),
                    roomContext: this._getRoomContext(),
                }),
            },
        ) || { metadata };
        const webhookUrl = (this._vapiWebhookUrl || '').toString().trim();
        const reportToken = (this._vapiReportToken || '').toString().trim();
        if (!webhookUrl || !reportToken) return overrides;
        const joiner = webhookUrl.includes('?') ? '&' : '?';
        overrides.server = {
            url: `${webhookUrl}${joiner}token=${encodeURIComponent(reportToken)}`,
            timeoutSeconds: 30,
            headers: { Authorization: `Bearer ${reportToken}` },
        };
        return overrides;
    }

    _resolveSaveTranscriptUrl() {
        const fromToken = (this._vapiSaveTranscriptUrl || '').toString().trim();
        if (fromToken) return fromToken;
        const base = (this.model?.config?.baseWebUrl || (typeof window !== 'undefined' && window.location?.origin) || '').toString().replace(/\/$/, '');
        return base ? `${base}/vapi-agent/save-transcript` : '/vapi-agent/save-transcript';
    }

    _persistCallReport(callEndData = {}, { keepalive = false, isRetry = false } = {}) {
        if (this._isEditMode || this.state?.mode === 'edit') {
            console.warn('[VAPI] skip save transcript: edit mode');
            return;
        }
        if (!this._vapiReportToken) {
            console.warn('[VAPI] skip save transcript: missing reportToken from /vapi-agent/token');
            return;
        }
        // Live SDK messages are the source of truth. Transcription UI is optional fallback only.
        this._hydrateCallTranscriptHistoryFromConversation();
        this._hydrateCallTranscriptHistoryFromTranscription();
        const history = Array.isArray(this._callTranscriptHistory) ? this._callTranscriptHistory : [];
        if (!history.length && !this._vapiCallId && !this._vapiLocalId) {
            console.warn('[VAPI] skip save transcript: no call id or transcript yet');
            return;
        }
        const transcript = history
            .map(({ role, text }) => `${role === 'user' ? 'User' : 'Assistant'}: ${String(text || '').trim()}`)
            .filter((line) => !line.endsWith(':'))
            .join('\n\n');
        const url = this._resolveSaveTranscriptUrl();
        const waitForSummary = this._isSummarizeCallEnd(callEndData) && !keepalive;
        const body = {
            reportToken: this._vapiReportToken,
            callId: this._vapiCallId || null,
            localId: this._vapiLocalId || null,
            sessionString: this._getVisitSessionString(),
            aiToken: this._aiTokenValue || getAiTokenFromSearch(this.model?.urlParams) || null,
            assistantId: (this.dbConfig?.VAPI_ASSISTANT_ID || this.dbConfig?.VAPI_SQUAD_ID || '').trim(),
            durationSec: this._lastCallDurationSec,
            endedReason: this._endedReasonFromCallEnd(callEndData),
            transcript,
            messages: history.map((entry) => ({
                role: entry.role,
                text: entry.text,
                isFinal: !!entry.isFinal,
            })),
            startedAt: this._lastCallStartedAt || null,
            endedAt: this._lastCallEndedAt || Date.now(),
            waitForSummary: waitForSummary,
        };
        const json = JSON.stringify(body);
        const started = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        if (waitForSummary) {
            console.log('[VAPI] summarize wait start', { callId: body.callId, retry: !!isRetry });
        }
        const saveRequest = fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this._vapiReportToken}`,
            },
            body: json,
            keepalive: !!keepalive,
        }).then((res) => {
            if (!res.ok) {
                console.warn('[VAPI] save transcript HTTP', res.status, url);
                return null;
            }
            return res.json().catch(() => null);
        }).then((data) => {
            const summaryLen = String(data?.summary || '').length;
            if (waitForSummary) {
                const ms = Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - started);
                console.log('[VAPI] summarize took', `${ms}ms`, {
                    retry: !!isRetry,
                    hasSummary: summaryLen > 0,
                    serverMs: data?.summarizeMs ?? null,
                    summaryChars: summaryLen,
                });
            }
            if (data) {
                this._rememberSessionConversation(data.summary, data.memory, body.sessionString);
                if (callEndData?.agentSceneChange) this._stashSummaryHandoff();
                if (waitForSummary && this.model) this.model._aiTokenPromptCache = null;
            }
            if (waitForSummary && !isRetry && !keepalive && summaryLen === 0) {
                this._scheduleCallReportRetry(callEndData);
            }
        }).catch((err) => {
            if (waitForSummary) {
                const ms = Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - started);
                console.warn('[VAPI] summarize failed after', `${ms}ms`, err);
                if (!isRetry && !keepalive) this._scheduleCallReportRetry(callEndData);
            } else {
                console.warn('[VAPI] save transcript failed', err);
            }
        });
        if (waitForSummary) {
            this._sessionMemoryPending = true;
            this._trackSummarizeRequest(saveRequest);
        }
    }

    _maybeShowTalkSummaryModal(callEndData = {}) {
        if (!this.dbConfig?.promptTranscriptModal) return;
        if (callEndData?.failed) return;
        if (callEndData?.sceneChange) return;
        // User hang-up and natural VAPI end are intentional; other cancelled ends (connect abort, etc.) are not.
        if (callEndData?.cancelled && !callEndData?.userEnded && !callEndData?.normal) return;
        const snapshot = this._snapshotTalkSummary();
        if (!snapshot) return;
        // Defer so an in-flight "End call?" confirm can close without hiding this modal.
        setTimeout(() => {
            if (!this.dbConfig?.promptTranscriptModal) return;
            this._showTalkSummaryModal(snapshot);
        }, 0);
    }

    _buildNode() {
        const w = this._domW = this._initWidth ?? this.dbConfig?.width ?? 480;
        const h = this._domH = this._initHeight ?? this.dbConfig?.height ?? 360;
        this._vapiChairInstance = new VapiChair({ width: w, height: h, dbConfig: this.dbConfig, meshId: this.id });
        const chairContent = this._vapiChairInstance.render();
        chairContent.style.width = '100%';
        chairContent.style.height = '100%';
        const chairSurfaceEl = chairContent.querySelector('.vrai-agent-chair');
        const statusEl = chairContent.querySelector('.vrai-status-agent');
        if (statusEl && this._isEditMode) {
            statusEl.textContent = (typeof $trans !== 'undefined' && $trans('status-of-agent')) || 'Status of agent';
        }
        if (statusEl && this.dbConfig?.chairStatusSize) {
            statusEl.style.fontSize = `${this.dbConfig.chairStatusSize}rem`;
        }
        if (this._isEditMode) {
            // Edit mode behavior like VapiChairTest: only move/perspective interactions.
            chairContent.style.pointerEvents = 'none';
        } else if (chairSurfaceEl) {
            // The visible chair surface should be orbitable and never participate in hover/click state.
            chairSurfaceEl.style.cursor = 'default';
            chairSurfaceEl.style.pointerEvents = 'none';
        }

        const dataId = `vrai_agent_${this.id}`;
        // Ensure a single DOM node per chair id to prevent visual doubling.
        const staleNodes = Array.from(document.querySelectorAll(`[data-id='${dataId}']`));
        staleNodes.forEach(node => node?.remove?.());

        const root = document.createElement('div');
        root.setAttribute('class', 'vrai-agent-component video_element css3d_test_wrapper vrai-agent-2d-indicator');
        root.setAttribute('data-css3d-interactive', 'true');
        root.setAttribute(
            'data-css3d-click-target',
            this._isEditMode
                ? '.vrai-agent-chair-perspective-content, .vrai-agent-chair-corner-handle'
                : '.vrai-agent-chair-indicator-el, .vrai-agent-chair-end-indicator'
        );
        root.setAttribute('data-id', dataId);
        root.style.cssText = `
            position: relative;
            pointer-events: none;
            will-change: transform;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: geometricPrecision;
            user-select: none;
            -webkit-user-select: none;
        `;
        root.style.opacity = '1';
        root.classList.add(`vrai-agent-2d-${this.id}`);
        root.classList.add('vapi-chair-test-plane');
        root.style.width = w + 'px';
        root.style.height = h + 'px';

        if (this.state.mode !== 'edit') {
            root.style.cursor = 'default';
        }

        root.addEventListener('pointerdown', (e) => {
            if (e.button !== 0) return;
            if (e.target?.closest?.('.vrai-agent-chair-indicator-el') || e.target?.closest?.('.vrai-agent-chair-end-indicator')) return;
            if (e.target?.closest?.('.vrai-agent-chair-corner-handle')) return;
            const hitPerspectiveContent = e.target?.closest?.('.vrai-agent-chair-perspective-content')
                ?? document.elementFromPoint(e.clientX, e.clientY)?.closest?.('.vrai-agent-chair-perspective-content');
            if (this.state.mode === 'edit') {
                if (!hitPerspectiveContent) return;
                e.preventDefault();
                e.stopPropagation();
                if (this._onPointerDown) this._onPointerDown(e);
            }
        });

        // After pointerup we open the settings panel; the synthesized click still bubbles to
        // #css3d. Raycast misses the tiny anchor mesh, so ModeEdit would call deselectElement()
        // and close the menu unless we stop propagation here (same pattern as pointerdown).
        if (this._isEditMode) {
            root.addEventListener('click', (e) => {
                e.stopPropagation();
            });
        }

        const saved = this.dbConfig?.perspectiveOffsets;
        this._perspectiveCorners = Array.isArray(saved) && saved.length === 4
            ? saved.map(p => p.xr !== undefined
                ? { x: p.xr * w, y: p.yr * h }
                : { x: Number(p.x), y: Number(p.y) })
            : [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];

        const perspectiveContent = document.createElement('div');
        perspectiveContent.className = 'vrai-agent-chair-perspective-content';
        perspectiveContent.style.cssText = `position:absolute;inset:0;overflow:visible;transform-origin:0 0;transform-style:flat;will-change:transform;pointer-events:${this._isEditMode ? 'auto' : 'none'};`;
        if (this._isEditMode) perspectiveContent.style.cursor = 'grab';
        perspectiveContent.appendChild(chairContent);
        this._perspectiveContent = perspectiveContent;

        const perspectiveWrapper = document.createElement('div');
        perspectiveWrapper.className = 'vrai-agent-chair-perspective-wrapper';
        perspectiveWrapper.style.cssText = 'position:absolute;inset:0;overflow:visible;pointer-events:none;';
        this._perspectiveWrapper = perspectiveWrapper;
        perspectiveWrapper.appendChild(perspectiveContent);

        this._applyPerspectiveTransform = () => {
            const ew = root.offsetWidth || this._domW;
            const eh = root.offsetHeight || this._domH;
            if (!this._hasFinitePerspectiveCorners()) {
                this._perspectiveCorners = [{ x: 0, y: 0 }, { x: ew, y: 0 }, { x: ew, y: eh }, { x: 0, y: eh }];
                this._syncHandlePositions?.();
            }
            const currentOrigPos = [{ x: 0, y: 0 }, { x: ew, y: 0 }, { x: ew, y: eh }, { x: 0, y: eh }];
            const m = computeProjectiveMatrix3d(currentOrigPos, this._perspectiveCorners);
            perspectiveContent.style.transform = m;
            // Same `top` in edit and view so saved positions match on screen (avoid view-only nudge).
            root.style.setProperty('top', this._rootTopOffset, 'important');
            root.style.clipPath = '';
            this._syncChairOverlayPositions();
        };
        this._applyPerspectiveTransform();

        // Fixed on-screen size: base px below is compensated by _syncCornerHandleInverseScale (CSS3DObject scale).
        this._cornerHandleBasePx = 24;

        this._cornerHandles = [];
        for (let i = 0; i < 4; i++) {
            const hnd = document.createElement('div');
            hnd.className = 'vrai-agent-chair-corner-handle';
            hnd.dataset.corner = String(i);
            hnd.style.cssText = `position:absolute;width:${this._cornerHandleBasePx}px;height:${this._cornerHandleBasePx}px;border-radius:50%;border:2px solid #0af;background:rgba(0,170,255,0.4);cursor:grab;transform:translate(-50%,-50%);transform-origin:center center;z-index:3;pointer-events:auto;box-sizing:border-box;`;
            const updateHandlePos = () => { hnd.style.left = this._perspectiveCorners[i].x + 'px'; hnd.style.top = this._perspectiveCorners[i].y + 'px'; };
            updateHandlePos();
            this._cornerHandles.push(hnd);
            const onCornerPointerDown = (e) => {
                if (this.dbConfig?.lockedInEdit) return;
                e.preventDefault();
                e.stopPropagation();
                hnd.style.cursor = 'grabbing';
                const startX = e.clientX, startY = e.clientY;
                const startPos = { x: this._perspectiveCorners[i].x, y: this._perspectiveCorners[i].y };
                const onMove = (ev) => {
                    const dx = ev.clientX - startX, dy = ev.clientY - startY;
                    this._perspectiveCorners[i].x = startPos.x + dx;
                    this._perspectiveCorners[i].y = startPos.y + dy;
                    updateHandlePos();
                    this._applyPerspectiveTransform();
                };
                const onUp = () => {
                    hnd.style.cursor = 'grab';
                    document.removeEventListener('pointermove', onMove);
                    document.removeEventListener('pointerup', onUp);
                    const ew = root.offsetWidth || this._domW;
                    const eh = root.offsetHeight || this._domH;
                    this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveCorners.map(p => ({ x: p.x, y: p.y, xr: p.x / ew, yr: p.y / eh })) };
                    if (this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
                };
                document.addEventListener('pointermove', onMove);
                document.addEventListener('pointerup', onUp);
            };
            hnd.addEventListener('pointerdown', onCornerPointerDown);
            if (this.state.mode !== 'edit') hnd.style.display = 'none';
            perspectiveWrapper.appendChild(hnd);
        }

        this._syncHandlePositions = () => {
            this._cornerHandles.forEach((hnd, i) => {
                if (this._perspectiveCorners[i]) {
                    hnd.style.left = this._perspectiveCorners[i].x + 'px';
                    hnd.style.top = this._perspectiveCorners[i].y + 'px';
                }
            });
        };

        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver((entries) => {
                const entry = entries[0];
                if (!entry) return;
                const newW = entry.contentRect.width;
                const newH = entry.contentRect.height;
                if (newW <= 0 || newH <= 0) return;
                const oldW = this._domW;
                const oldH = this._domH;
                if (newW !== oldW || newH !== oldH) {
                    const scaleX = newW / oldW;
                    const scaleY = newH / oldH;
                    this._perspectiveCorners.forEach(p => { p.x *= scaleX; p.y *= scaleY; });
                    this._domW = newW;
                    this._domH = newH;
                }
                this._syncHandlePositions();
                this._applyPerspectiveTransform();
            });
            this._resizeObserver.observe(root);
        }

        root.appendChild(perspectiveWrapper);

        const chairIndicatorEl = chairContent.querySelector('.vrai-agent-chair-indicator-el');
        const chairEndEl = chairContent.querySelector('.vrai-agent-chair-end-indicator-el');
        const legacyChairEndBtn = chairContent.querySelector('.vrai-agent-chair-btn-end');
        const floatingOverlayHost = document.body;
        this._floatingOverlayHost = floatingOverlayHost;
        document.querySelectorAll(`[data-vrai-agent-overlay='${dataId}']`).forEach(node => node?.remove?.());
        legacyChairEndBtn?.remove?.();
        if (this.state.mode === 'edit' && chairEndEl) {
            chairEndEl.remove();
        }
        if (floatingOverlayHost) {
            if (chairIndicatorEl) {
                floatingOverlayHost.appendChild(chairIndicatorEl);
                chairIndicatorEl.setAttribute('data-vrai-agent-overlay', dataId);
                chairIndicatorEl.style.position = 'fixed';
                chairIndicatorEl.style.right = 'auto';
                chairIndicatorEl.style.bottom = 'auto';
                chairIndicatorEl.style.margin = '0';
                chairIndicatorEl.style.display = 'grid';
                chairIndicatorEl.style.opacity = '0';
                chairIndicatorEl.style.pointerEvents = 'none';
                chairIndicatorEl.style.transformStyle = 'flat';
                chairIndicatorEl.style.backfaceVisibility = 'hidden';
                chairIndicatorEl.style.transform = 'none';
                chairIndicatorEl.style.backgroundColor = 'transparent';
                this._chairIndicatorEl = chairIndicatorEl;
                chairIndicatorEl.addEventListener('pointerenter', this._handleFloatingOverlayPointerEnter);
                chairIndicatorEl.addEventListener('pointerleave', this._handleFloatingOverlayPointerLeave);
            }
            if (chairEndEl && this.state.mode !== 'edit') {
                floatingOverlayHost.appendChild(chairEndEl);
                chairEndEl.setAttribute('data-vrai-agent-overlay', dataId);
                chairEndEl.style.position = 'fixed';
                chairEndEl.style.right = 'auto';
                chairEndEl.style.bottom = 'auto';
                chairEndEl.style.margin = '0';
                chairEndEl.style.display = 'grid';
                chairEndEl.style.opacity = '0';
                chairEndEl.style.pointerEvents = 'none';
                chairEndEl.style.transformStyle = 'flat';
                chairEndEl.style.backfaceVisibility = 'hidden';
                chairEndEl.style.transform = 'none';
                chairEndEl.style.backgroundColor = 'transparent';
                const endCircle = chairEndEl.querySelector('.circle-wrapper');
                if (endCircle) {
                    endCircle.style.backgroundColor = '#e74c3c';
                    endCircle.style.border = 'none';
                    endCircle.style.boxShadow = 'none';
                }
                const endIcon = chairEndEl.querySelector('.material-icons-outlined');
                if (endIcon) {
                    endIcon.style.color = '#ffffff';
                }
                this._chairEndIndicatorEl = chairEndEl;
                chairEndEl.addEventListener('pointerenter', this._handleFloatingOverlayPointerEnter);
                chairEndEl.addEventListener('pointerleave', this._handleFloatingOverlayPointerLeave);
            }
            this._syncChairOverlayPositions();
            this._syncFloatingOverlayState();
        }

        this.root = root;
        this.dom = root;
        this._syncTextProportionScale();
        this.css3dObject = new CSS3DObject(root);
        this.css3dObject.scale.set(this._css3dScale, this._css3dScale, 1);
        this._syncCornerHandleInverseScale();

        const geo = new BoxGeometry(0.1, 0.1, 0.1);
        const mat = new MeshBasicMaterial({ visible: false });
        this.anchor = new Mesh(geo, mat);
        this.anchor.name = 'vraiAgent';
        this.anchor.type = 'vraiAgent';
        this.anchor.groupDetails = {
            title: (typeof $trans !== 'undefined' && $trans('vrai-agent-text')) || 'AI Agent',
            icon: 'support_agent',
            editTitle: (typeof $trans !== 'undefined' && $trans('edit-vrai-agent')) || 'Edit AI Agent',
            dataLang: 'edit-vrai-agent'
        };
        this.anchor.add(this.css3dObject);

        this.elementClass = `vrai-agent-2d-${this.id}`;
        this.mesh = this.anchor;
        this.object = this.anchor;

        requestAnimationFrame(() => this._syncChairOverlayPositions());
    }

    /**
     * Keep text proportions stable against the chair base size (root width),
     * so labels remain visually proportional when the component dimensions change.
     */
    _syncTextProportionScale() {
        if (!this.root) return;
        const baseWidth = VRVapiChairWithCSS3D.BASE_WIDTH_PX;
        const width = Number(this._domW || this.root.offsetWidth || baseWidth);
        const ratio = Number.isFinite(width) && width > 0 ? (width / baseWidth) : 1;
        this.root.style.setProperty('--vrai-chair-proportion-scale', `${ratio}`);
    }

    /**
     * Keep overlays outside the entire CSS3D root so they stay plain viewport UI.
     * The start indicator uses a fixed offset; the end-call control still tracks the
     * visual center of the warped content.
     */
    _getVisibleChairViewportRect() {
        if (!this._perspectiveContent || !this.anchor || !this.camera || this.anchor.visible === false) return null;

        this.camera.updateMatrixWorld?.(true);
        this.anchor.updateMatrixWorld?.(true);

        const anchorWorldPos = this.anchor.getWorldPosition(new Vector3());
        const cameraWorldPos = this.camera.getWorldPosition(new Vector3());
        const toChair = anchorWorldPos.clone().sub(cameraWorldPos);
        if (toChair.lengthSq() <= 1e-6) return null;

        const cameraDirection = this.camera.getWorldDirection(new Vector3());
        if (cameraDirection.dot(toChair.normalize()) <= 0.05) return null;

        this._viewProjectionMatrix.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);
        this._cameraFrustum.setFromProjectionMatrix(this._viewProjectionMatrix);
        if (!this._cameraFrustum.containsPoint(anchorWorldPos)) return null;

        const projected = anchorWorldPos.clone().project(this.camera);
        if (![projected.x, projected.y, projected.z].every(Number.isFinite)) return null;
        const ndcPadding = 1.05;
        if (
            projected.x < -ndcPadding || projected.x > ndcPadding
            || projected.y < -ndcPadding || projected.y > ndcPadding
            || projected.z < -1 || projected.z > 1
        ) return null;

        const pr = this._perspectiveContent.getBoundingClientRect();
        if (pr.width < 0.5 || pr.height < 0.5) return null;

        const viewportRect = this._viewportEl?.getBoundingClientRect?.() || {
            left: 0,
            top: 0,
            right: window.innerWidth,
            bottom: window.innerHeight,
        };
        const intersectsViewport = pr.right > viewportRect.left
            && pr.left < viewportRect.right
            && pr.bottom > viewportRect.top
            && pr.top < viewportRect.bottom;

        return intersectsViewport ? pr : null;
    }

    /** Project chair anchor into viewport coordinates for stable center alignment. */
    _getChairOverlayAnchorPoint() {
        if (!this.anchor || !this.camera || this.anchor.visible === false) return null;
        const rect = this._viewportEl?.getBoundingClientRect?.() || {
            left: 0,
            top: 0,
            width: window.innerWidth,
            height: window.innerHeight,
            right: window.innerWidth,
            bottom: window.innerHeight,
        };
        const width = rect.width || window.innerWidth;
        const height = rect.height || window.innerHeight;
        if (width < 1 || height < 1) return null;
        const worldPosition = this.anchor.getWorldPosition(new Vector3());
        const projected = worldPosition.clone().project(this.camera);
        if (![projected.x, projected.y, projected.z].every(Number.isFinite)) return null;
        if (projected.z < -1 || projected.z > 1) return null;
        return {
            x: rect.left + ((projected.x + 1) * 0.5 * width),
            y: rect.top + ((1 - projected.y) * 0.5 * height),
        };
    }

    _positionFloatingOverlayAtPoint(el, point) {
        if (!el || !point) return;
        const w = el.offsetWidth || 64;
        const h = el.offsetHeight || 64;
        const offsetX = Number(this.dbConfig?.indicatorOffsetX ?? 0) || 0;
        const offsetY = Number(this.dbConfig?.indicatorOffsetY ?? 0) || 0;
        el.style.left = `${point.x - (w * 0.5) + offsetX}px`;
        el.style.top = `${point.y - (h * 0.5) + offsetY}px`;
        el.style.right = 'auto';
        el.style.bottom = 'auto';
    }

    _syncChairOverlayPositions() {
        if (!this._chairIndicatorEl && !this._chairEndIndicatorEl) return;
        const anchorPoint = this._getChairOverlayAnchorPoint();
        if (!anchorPoint) return;
        this._positionFloatingOverlayAtPoint(this._chairIndicatorEl, anchorPoint);
        this._positionFloatingOverlayAtPoint(this._chairEndIndicatorEl, anchorPoint);
    }

    /** Called after CSS3DRenderer.render so overlay positions match the warped chair on screen. */
    syncFloatingOverlays() {
        this._syncChairOverlayPositions();
        this._syncFloatingOverlayState();
    }

    _handleFloatingOverlayPointerEnter = () => {
        // Floating controls should not resurrect hover styling on the hidden/non-interactive chair plane.
    };

    _handleFloatingOverlayPointerLeave = (e) => {
        if (this.dom?.contains?.(e.relatedTarget)) return;
        if (
            e.relatedTarget?.closest?.('.vrai-agent-chair-indicator-el')
            || e.relatedTarget?.closest?.('.vrai-agent-chair-end-indicator')
        ) {
            return;
        }
        this.dom?.querySelector('.vrai-agent-chair')?.classList.remove('is-hovered');
    };

    _isSelectedInEditMode() {
        const selected = this.model?.view?.elementSelected;
        if (!selected) return false;
        if (selected === this) return true;
        if (selected?.parent === this) return true;
        if (selected?.userData?.vraiAgentChair === this) return true;
        return false;
    }

    _syncFloatingOverlayState() {
        const hasVisibleRect = !!this._getVisibleChairViewportRect();
        const inEditMode = this.state.mode === 'edit';
        const shouldRenderOverlay = inEditMode || (this.state.mode !== 'edit' && hasVisibleRect);
        const showStart = inEditMode
            ? (shouldRenderOverlay && this._isSelectedInEditMode())
            : (shouldRenderOverlay && !(this._vapiConnecting || this._vapiCallActive));
        const showEnd = shouldRenderOverlay && !!(this._vapiConnecting || this._vapiCallActive);

        if (!shouldRenderOverlay) {
            this.dom?.querySelector('.vrai-agent-chair')?.classList.remove('is-hovered');
        }

        if (this._chairIndicatorEl) {
            this._chairIndicatorEl.style.display = shouldRenderOverlay ? 'grid' : 'none';
            this._chairIndicatorEl.style.opacity = showStart ? '1' : '0';
            this._chairIndicatorEl.style.pointerEvents = (!inEditMode && showStart) ? 'auto' : 'none';
        }
        if (this._chairEndIndicatorEl) {
            this._chairEndIndicatorEl.style.display = shouldRenderOverlay ? 'grid' : 'none';
            this._chairEndIndicatorEl.style.opacity = showEnd ? '1' : '0';
            this._chairEndIndicatorEl.style.pointerEvents = showEnd ? 'auto' : 'none';
        }
    }

    _isPlaceholderAgentImage(src) {
        if (!src || String(src).includes('empty')) return true;
        try {
            if (src === VapiChair.getDefaultChairImgSrc()) return true;
        } catch (e) { /* no-op */ }
        return false;
    }

    _perspectiveOffsetsForDb() {
        const ew = this.root?.offsetWidth || this._domW || 1;
        const eh = this.root?.offsetHeight || this._domH || 1;
        return (this._perspectiveCorners || []).map(p => ({
            x: p.x, y: p.y, xr: p.x / ew, yr: p.y / eh
        }));
    }

    _hasFinitePerspectiveCorners(corners = this._perspectiveCorners) {
        return Array.isArray(corners)
            && corners.length === 4
            && corners.every((p) => Number.isFinite(Number(p?.x)) && Number.isFinite(Number(p?.y)));
    }

    _resetPerspectiveToRect(w, h, { persist = false } = {}) {
        if (!this._perspectiveCorners) return;
        const width = Number(w) > 0 ? Number(w) : (this.root?.offsetWidth || this._domW || 480);
        const height = Number(h) > 0 ? Number(h) : (this.root?.offsetHeight || this._domH || 360);
        this._perspectiveCorners.splice(0, 4, ...[
            { x: 0, y: 0 },
            { x: width, y: 0 },
            { x: width, y: height },
            { x: 0, y: height }
        ]);
        this._syncHandlePositions?.();
        this._applyPerspectiveTransform?.();
        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveOffsetsForDb() };
        if (persist && this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
    }

    /**
     * Resize the CSS3D root to match a loaded agent image; scales perspective corners like a resize.
     */
    _setRootPixelSize(newW, newH, { persist = true } = {}) {
        if (!this.root || newW <= 0 || newH <= 0) return;
        const oldW = Number(this._domW);
        const oldH = Number(this._domH);
        const hasSafeOldSize = Number.isFinite(oldW) && Number.isFinite(oldH) && oldW > 0.5 && oldH > 0.5;
        if (!hasSafeOldSize || !this._hasFinitePerspectiveCorners()) {
            this._resetPerspectiveToRect(newW, newH, { persist });
            return;
        }
        const scaleX = newW / oldW;
        const scaleY = newH / oldH;
        if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY) || scaleX <= 0 || scaleY <= 0 || scaleX > 20 || scaleY > 20) {
            this._resetPerspectiveToRect(newW, newH, { persist });
            return;
        }
        if (this._perspectiveCorners) {
            this._perspectiveCorners.forEach(p => { p.x *= scaleX; p.y *= scaleY; });
        }
        this._domW = newW;
        this._domH = newH;
        if (this._resizeObserver) this._resizeObserver.disconnect();
        this.root.style.width = `${newW}px`;
        this.root.style.height = `${newH}px`;
        if (this._vapiChairInstance) {
            this._vapiChairInstance.width = newW;
            this._vapiChairInstance.height = newH;
        }
        this._syncTextProportionScale();
        this.dbConfig = {
            ...this.dbConfig,
            width: newW,
            height: newH,
            perspectiveOffsets: this._perspectiveOffsetsForDb(),
        };
        this._syncHandlePositions?.();
        this._applyPerspectiveTransform?.();
        if (this._resizeObserver) this._resizeObserver.observe(this.root);
        if (persist && this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
    }

    _scheduleFitRootToChairImage() {
        const img = this.dom?.querySelector?.('.vrai-agent-chair-img');
        if (!img) return;
        const apply = () => {
            if (this._isPlaceholderAgentImage(img.currentSrc || img.src)) return;
            const nw = img.naturalWidth;
            const nh = img.naturalHeight;
            if (!nw || !nh) return;
            const maxDim = VRVapiChairWithCSS3D.MAX_AGENT_IMAGE_DIMENSION_PX;
            const largestSide = Math.max(nw, nh);
            const needsClamp = largestSide > maxDim;
            const ratio = needsClamp ? (maxDim / largestSide) : 1;
            const targetW = Math.max(1, Math.round(nw * ratio));
            const targetH = Math.max(1, Math.round(nh * ratio));
            const shouldResetPerspective = this._resetPerspectiveOnNextImageFit;
            this._setRootPixelSize(targetW, targetH, { persist: !shouldResetPerspective });
            if (shouldResetPerspective) {
                this._resetPerspectiveToRect(targetW, targetH, { persist: true });
                this._resetPerspectiveOnNextImageFit = false;
            }
        };
        if (img.complete && img.naturalWidth) {
            apply();
        } else {
            img.addEventListener('load', apply, { once: true });
            img.addEventListener('error', () => { }, { once: true });
        }
    }

    _clientToNDC(clientX, clientY) {
        const el = this._viewportEl;
        if (!el) {
            this._ndc.x = (clientX / this._sizes.width) * 2 - 1;
            this._ndc.y = -(clientY / this._sizes.height) * 2 + 1;
            return;
        }
        const rect = el.getBoundingClientRect();
        this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this._ndc.y = 1 - ((clientY - rect.top) / rect.height) * 2;
    }

    _setFromClientXY(clientX, clientY) {
        this._clientToNDC(clientX, clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        const camWorld = this.camera.getWorldPosition(new Vector3());
        // Apply the grab offset so the object follows the cursor without jumping.
        const dir = this._raycaster.ray.direction.clone().normalize().add(this._dragDirOffset).normalize();
        this._tmpVec.copy(dir)
            .multiplyScalar(this._dragFixedDistance)
            .add(camWorld);
        if (this.anchor.parent) {
            this.anchor.parent.updateMatrixWorld(true);
            this.anchor.parent.worldToLocal(this._tmpVec);
            this.anchor.position.copy(this._tmpVec);
        } else {
            this.anchor.position.copy(this._tmpVec);
        }
    }

    _onPointerMove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDragging) return;
        this._setFromClientXY(e.clientX, e.clientY);
        // Keep facing camera continuously while dragging.
        this.anchor.lookAt(this.camera.position);
    };

    _onPointerUp = (e) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
        if (this._captureEl) {
            try { this._captureEl.releasePointerCapture(e.pointerId); } catch (err) { }
            this._captureEl = null;
        }
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = true;
        if (this.model?.view) {
            if (this.model.view.updateJSON) this.model.view.updateJSON(this.anchor, null);
            this.model.view.elementSelected = this;
            if (typeof $ !== 'undefined') {
                $("#elementSelected").val(this.getId?.() ?? this.id);
            }
            this.model.view._updateVraiAgentChairsVisibility?.();
            this.showObjectMenu();
            if (typeof $ !== 'undefined') $('.edit_element').trigger('click');
        }
    };

    _onPointerDown = (e) => {
        if (this.state.mode !== 'edit') return;
        if (this.dbConfig?.lockedInEdit) return;
        if (this.isDragging) return;
        if (e.target && (e.target.closest('input') || e.target.closest('textarea') || e.target.closest('button') || e.target.closest('a'))) return;
        if (e.target?.closest?.('.vrai-agent-chair-corner-handle')) return;
        // Match css3dperspective: the plane drag starts from the object position; cursor offset should not affect
        // the final position on the sphere.
        this._dragOffsetX = 0;
        this._dragOffsetY = 0;
        this.isDragging = true;
        this._captureEl = e.currentTarget || this.dom;
        try { this._captureEl?.setPointerCapture?.(e.pointerId); } catch (err) { }
        // Lock distance and capture directional offset so the object doesn't jump on first move.
        if (this.anchor.parent) {
            this.anchor.parent.updateMatrixWorld(true);
            this.anchor.getWorldPosition(this._tmpVec);
        } else {
            this._tmpVec.copy(this.anchor.position);
        }
        const camWorld = this.camera.getWorldPosition(new Vector3());
        this._dragFixedDistance = camWorld.distanceTo(this._tmpVec);
        // Direction from camera to object (normalised).
        const objDir = this._tmpVec.clone().sub(camWorld).normalize();
        // Direction from camera through the cursor click point (normalised).
        this._clientToNDC(e.clientX, e.clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        const cursorDir = this._raycaster.ray.direction.clone().normalize();
        // Store the angular offset so every move frame adds it back.
        this._dragDirOffset.copy(objDir).sub(cursorDir);
        this._dragGrabOffset.set(0, 0, 0);
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grabbing';
        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);
        window.addEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = false;
    };

    _attachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
        // Entering edit mode: show corner handles.
        this._cornerHandles?.forEach(h => { h.style.display = ''; });
    }

    _detachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = '';
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        this.isDragging = false;
        if (this.controls) this.controls.enabled = true;
        // Leaving edit mode: hide corner handles without changing alignment.
        this._cornerHandles?.forEach(h => { h.style.display = 'none'; });
        if (this._applyPerspectiveTransform) this._applyPerspectiveTransform();
    }

    _getViewportScale() {
        const vpEl = this._viewportEl;
        const canvasH = vpEl?.clientHeight || 0;
        const winH = window.innerHeight;
        return (canvasH > 0 && winH > canvasH) ? winH / canvasH : 1;
    }

    /**
     * Counteract CSS3DObject scale so corner handles stay a fixed apparent size on screen.
     */
    _syncCornerHandleInverseScale() {
        if (!this.css3dObject) return;
        const s = Math.max(this.css3dObject.scale.x || 1, 0.02);
        const inv = 1 / s;
        if (this._cornerHandles?.length) {
            const t = `translate(-50%,-50%) scale(${inv})`;
            for (const hnd of this._cornerHandles) {
                hnd.style.transform = t;
            }
        }
        if (this._chairIndicatorEl) {
            this._chairIndicatorEl.style.transformOrigin = 'top left';
            this._chairIndicatorEl.style.setProperty('transform', 'none', 'important');
            this._chairIndicatorEl.style.transformStyle = 'flat';
        }
        if (this._chairEndIndicatorEl) {
            this._chairEndIndicatorEl.style.transformOrigin = 'top left';
            this._chairEndIndicatorEl.style.setProperty('transform', 'none', 'important');
            this._chairEndIndicatorEl.style.transformStyle = 'flat';
        }
    }

    render(camera) {
        if (!camera) return;
        if (!this.isDragging) {
            this.anchor.lookAt(camera.position);
            this._css3dScale = this._getViewportScale();
            const scale = this._css3dScale * (this._userScale ?? 1);
            if (this.css3dObject) this.css3dObject.scale.set(scale, scale, 1);
        }
        this._syncCornerHandleInverseScale();
        // Keep DOM visibility in sync with anchor visibility so external show/hide
        // code (actions/ticks) can't leave the chair in an inconsistent state.
        this._syncDomVisibilityToAnchor();
    }

    getId() {
        return this.id;
    }

    getDbConfig() {
        return this.dbConfig;
    }

    setDbConfig(config) {
        const sanitizedConfig = VRVapiChairWithCSS3D.sanitizeLegacySizeConfig(config || {});
        const shouldPreservePerspectiveOnImageSwap = this.state.mode !== 'edit'
            && this._preservePerspectiveOnNextImageUpdate === true
            && sanitizedConfig?.agent_image !== undefined;
        this.dbConfig = { ...this.dbConfig, ...sanitizedConfig };
        if (sanitizedConfig?.size !== undefined || sanitizedConfig?.scale !== undefined) {
            const s = sanitizedConfig.size ?? sanitizedConfig.scale;
            this._userScale = Number(s) > 0 ? Number(s) : 1;
        }
        if (sanitizedConfig?.width !== undefined || sanitizedConfig?.height !== undefined) {
            if (this.root) {
                if (sanitizedConfig.width !== undefined) this.root.style.width = sanitizedConfig.width + 'px';
                if (sanitizedConfig.height !== undefined) this.root.style.height = sanitizedConfig.height + 'px';
                this._domW = Number(sanitizedConfig.width ?? this._domW);
                this._domH = Number(sanitizedConfig.height ?? this._domH);
                this._syncTextProportionScale();
            }
        }
        if (!shouldPreservePerspectiveOnImageSwap
            && sanitizedConfig?.perspectiveOffsets
            && Array.isArray(sanitizedConfig.perspectiveOffsets)
            && this._perspectiveCorners) {
            const ew = this.root?.offsetWidth || this._domW || 1;
            const eh = this.root?.offsetHeight || this._domH || 1;
            const normalizedCorners = sanitizedConfig.perspectiveOffsets.slice(0, 4).map((p, i) => {
                const current = this._perspectiveCorners?.[i] || { x: i === 1 || i === 2 ? ew : 0, y: i >= 2 ? eh : 0 };
                const xFromAbs = Number(p?.x);
                const yFromAbs = Number(p?.y);
                const xFromRatio = Number(p?.xr) * ew;
                const yFromRatio = Number(p?.yr) * eh;
                const x = Number.isFinite(xFromAbs) ? xFromAbs : (Number.isFinite(xFromRatio) ? xFromRatio : current.x);
                const y = Number.isFinite(yFromAbs) ? yFromAbs : (Number.isFinite(yFromRatio) ? yFromRatio : current.y);
                return { x, y };
            });
            if (normalizedCorners.length === 4) {
                this._perspectiveCorners.splice(0, 4, ...normalizedCorners);
                this._syncHandlePositions?.();
                if (this._applyPerspectiveTransform) this._applyPerspectiveTransform();
            }
        }
        this._applyVisibilityFromConfig();
        const vapiChairInstance = this._vapiChairInstance || this.dom?.__vapiChairInstance;
        if (vapiChairInstance?.updateImage && sanitizedConfig?.agent_image !== undefined) {
            this._resetPerspectiveOnNextImageFit = false;
            vapiChairInstance.updateImage(this.dbConfig.agent_image);
            if (shouldPreservePerspectiveOnImageSwap) {
                // Keep current warp after the image source swaps in view mode.
                this._applyPerspectiveTransform?.();
            }
            this._preservePerspectiveOnNextImageUpdate = false;
        }
        if (vapiChairInstance?.updateChairLabels && (sanitizedConfig?.chairName !== undefined || sanitizedConfig?.chairScenario !== undefined)) {
            vapiChairInstance.updateChairLabels(this.dbConfig?.chairName, this.dbConfig?.chairScenario);
        }
        if (vapiChairInstance?.updateTextSizes && (sanitizedConfig?.chairNameSize !== undefined || sanitizedConfig?.chairScenarioSize !== undefined)) {
            vapiChairInstance.updateTextSizes(this.dbConfig?.chairNameSize, this.dbConfig?.chairScenarioSize);
        }
        const currentAgentId = this._getAgentId();
        const activeTopStatusAgentId = this.model?.view?._activeVapiTopStatusAgentId;
        if ((sanitizedConfig?.chairName !== undefined || sanitizedConfig?.name !== undefined)
            && currentAgentId != null
            && activeTopStatusAgentId != null
            && String(currentAgentId) === String(activeTopStatusAgentId)
            && this.hasActiveVapiSession()) {
            this.model?.view?._refreshVapiTopStatus?.(currentAgentId, this._getAgentDisplayName());
        }
    }

    getLimits() {
        const min = 280;
        const max = 800;
        const anchorDistance = Number(this.anchor?.position?.length?.());
        const configuredDistance = Number(this.dbConfig?.distance);
        const distance = Number.isFinite(anchorDistance) && anchorDistance > 0
            ? anchorDistance
            : (Number.isFinite(configuredDistance) && configuredDistance > 0 ? configuredDistance : 540);
        return { min, max, distance };
    }

    getAxisRotations(/* format */) {
        return { rotateX: 0, rotateY: 0 };
    }

    setDistance(val) {
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0 || !this.anchor?.position?.setLength) return;
        const { min, max } = this.getLimits();
        const clamped = Math.min(max, Math.max(min, n));
        this.anchor.position.setLength(clamped);
        this.dbConfig = { ...this.dbConfig, distance: clamped };
    }

    /** Visual scale of the chair plane (does not move anchor distance). Persisted as dbConfig.size. */
    setSize(val) {
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0) return;
        this._userScale = n;
        this.dbConfig = { ...this.dbConfig, size: n };
    }

    _applyVisibilityFromConfig() {
        const chairActive = this.dbConfig?.components?.chair?.active !== false;
        // Some editor actions may store hideOnStart nested; support both.
        const hideOnStart = this.dbConfig?.hideOnStart ?? this.dbConfig?.components?.chair?.hideOnStart;
        const inEditMode = !!this.model?.editMode || this.state.mode === 'edit';
        const hiddenByHideOnStart = !inEditMode && !!hideOnStart;
        this.anchor.visible = chairActive && !hiddenByHideOnStart;
        this._syncDomVisibilityToAnchor();
    }

    _syncDomVisibilityToAnchor() {
        if (!this.dom || !this.dom.style) return;
        const visible = this.anchor?.visible !== false;
        this.dom.classList.toggle('visible', visible);
        // Allow explicit external show actions to override "hide on start" styling.
        this.dom.classList.toggle('hide-on-start', !visible);
        this.dom.style.display = visible ? '' : 'none';
        this._syncFloatingOverlayState();
    }

    /** Scene placement id (matches worldEvents source_object_id). Prefer over dbConfig.id — switchAgent/library merges can overwrite dbConfig.id. */
    _getAgentId() {
        return this.id ?? this.dbConfig?.id ?? null;
    }

    _getAiEvents() {
        return getAiEventsFromConfig(this.dbConfig);
    }

    /**
     * Build vapi.start overrides from configured AI events, session tools,
     * the per-chair VAPI room prompt, the URL AI token prompt, and guest-user context.
     * Room AI only — not used for Space AI.
     */
    _buildAiEventAssistantOverrides() {
        const sessionOverrides = mergeVapiAssistantOverrides(
            buildVapiStoreSessionDataOverrides(),
            buildVapiCloseElementOverrides(),
        );
        const aiEvents = this._getAiEvents();
        const aiEventOverrides = aiEvents.length
            ? buildVapiAiEventAssistantOverrides(aiEvents)
            : null;
        const promptOverrides = buildVapiPromptVariableOverrides(
            getVapiRoomPromptFromConfig(this.dbConfig),
            this._getVapiExtraPrompt()
        );
        const guestOverrides = buildVapiGuestUserOverrides(isCurrentUserGuest(this.model));
        return mergeVapiAssistantOverrides(
            mergeVapiAssistantOverrides(sessionOverrides, aiEventOverrides),
            mergeVapiAssistantOverrides(promptOverrides, guestOverrides)
        );
    }

    _sendVapiSystemContext(vapi, content) {
        if (!content || !vapi) return;
        try {
            vapi.send({
                type: 'add-message',
                message: { role: 'system', content },
                triggerResponseEnabled: false,
            });
        } catch (err) {
            console.warn('[VAPI] system context inject failed', err);
        }
    }

    /** Inject extra system context once the websocket call is live. */
    _injectVapiCallContext(vapi) {
        this._sendVapiSystemContext(
            vapi,
            buildVapiRoomPromptSystemContent(getVapiRoomPromptFromConfig(this.dbConfig))
        );
        this._sendVapiSystemContext(
            vapi,
            buildVapiTokenPromptSystemContent(this._getVapiExtraPrompt())
        );
        this._sendVapiSystemContext(
            vapi,
            buildVapiGuestUserSystemContent(isCurrentUserGuest(this.model))
        );
    }

    _getSpaceContext() {
        return String(this.model?.sceneData?.name || this.model?.config?.worldName || '').trim();
    }

    _getRoomContext() {
        const scene = this.model?.currentScene;
        const sceneName = String(scene?.publicName || scene?.name || this.model?.currentSceneId || '').trim();
        const roomPrompt = getVapiRoomPromptFromConfig(this.dbConfig);
        return [sceneName, roomPrompt].filter(Boolean).join('\n');
    }

    _getAiTokenFromUrl() {
        return getAiTokenFromSearch(this.model?.urlParams);
    }

    _loadAiTokenPrompt() {
        const token = this._getAiTokenFromUrl();
        this._aiTokenValue = token || '';
        if (!token) {
            this._aiTokenPrompt = '';
            this._aiTokenMemory = null;
            return Promise.resolve(null);
        }
        if (this.model?._aiTokenPromptCache?.token === token) {
            this._aiTokenPrompt = this.model._aiTokenPromptCache.prompt || '';
            this._aiTokenMemory = this.model._aiTokenPromptCache.memory || null;
            return Promise.resolve(this.model._aiTokenPromptCache);
        }
        const worldId = ((this.model?.config?.worldId ?? this.model?.worldID ?? '') + '').trim();
        if (!worldId || !this.model?.worldFetcher?.resolveVapiAiToken) {
            this._aiTokenPrompt = '';
            this._aiTokenMemory = null;
            return Promise.resolve(null);
        }
        return new Promise((resolve) => {
            this.model.worldFetcher.resolveVapiAiToken(worldId, token, (data) => {
                const memory = data?.memory && typeof data.memory === 'object' ? data.memory : null;
                const cache = { token, prompt: data?.prompt || '', name: data?.name || '', memory };
                this.model._aiTokenPromptCache = cache;
                this._aiTokenPrompt = cache.prompt;
                this._aiTokenMemory = memory;
                resolve(cache);
            }, () => {
                this._aiTokenPrompt = '';
                this._aiTokenMemory = null;
                resolve(null);
            });
        });
    }

    _recordAiTokenCall() {
        const token = this._aiTokenValue || this._getAiTokenFromUrl();
        const worldId = ((this.model?.config?.worldId ?? this.model?.worldID ?? '') + '').trim();
        if (!token || !worldId || this._aiTokenCallRecorded) return;
        this._aiTokenCallRecorded = true;
        this.model?.worldFetcher?.recordVapiAiTokenCall?.(worldId, token);
    }

    _clipToolArguments(raw) {
        if (raw == null || raw === '') return undefined;
        let text = raw;
        if (typeof raw !== 'string') {
            try { text = JSON.stringify(raw); } catch (_) { text = String(raw); }
        }
        text = String(text).trim();
        if (!text || text === '{}') return undefined;
        return text.length > 200 ? text.slice(0, 200) : text;
    }

    /** Normalize Vapi tool-calls / function-call payloads into { name, toolCallId, arguments }. */
    _extractVapiToolCalls(message) {
        const out = [];
        const push = (name, id, args) => {
            const n = String(name || '').trim();
            if (!n) return;
            out.push({
                name: n,
                toolCallId: id || null,
                arguments: this._clipToolArguments(args),
                rawArguments: args,
            });
        };

        if (message?.type === 'function-call') {
            const fc = message.functionCall || message.function || {};
            push(fc.name, fc.id || message.id, fc.arguments || fc.parameters);
            return out;
        }

        const lists = [
            message?.toolCalls,
            message?.toolCallList,
            message?.toolWithToolCallList,
        ];
        for (const list of lists) {
            if (!Array.isArray(list)) continue;
            for (const call of list) {
                const fn = call?.function || call?.toolCall?.function || call;
                push(
                    fn?.name || call?.name || call?.toolCall?.name,
                    call?.id || call?.toolCallId || call?.toolCall?.id,
                    fn?.arguments || fn?.parameters || call?.arguments
                );
            }
        }

        const seen = new Set();
        return out.filter((call) => {
            const key = `${call.toolCallId || ''}:${call.name}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }

    _toolCallStartsSceneChange(call) {
        if (!call?.name || call.name === STORE_SESSION_DATA_TOOL_NAME || call.name === CLOSE_ELEMENT_TOOL_NAME) return false;
        const sourceId = this._getAgentId();
        const allowed = new Set(this._getAiEvents().map((e) => e.name));
        if (sourceId == null || !allowed.has(call.name)) return false;
        const events = this.model?.view?._getEventsForSourceByType?.(sourceId, call.name) || [];
        return events.some((ev) => ev?.action === 'goToScene');
    }

    _runVapiToolCall(call, { executeEvents = true } = {}) {
        const isStore = call.name === STORE_SESSION_DATA_TOOL_NAME;
        const isCloseElement = call.name === CLOSE_ELEMENT_TOOL_NAME;
        const parsedArgs = (isStore || isCloseElement)
            ? parseToolArguments(call.rawArguments ?? call.arguments)
            : null;
        const fieldKey = isStore ? slugifyFieldKey(parsedArgs?.field_key || parsedArgs?.fieldKey || 'vapi_summary') : undefined;
        const funnelStep = isStore
            ? String(parsedArgs?.funnel_step || parsedArgs?.funnelStep || fieldKey || '').trim()
            : undefined;
        const sourceId = this._getAgentId();
        const view = this.model?.view;
        const allowed = new Set(this._getAiEvents().map((e) => e.name));

        this._dispatchVapiEvent('vapi-agent-tool-call', {
            toolName: call.name,
            toolCallId: call.toolCallId,
            toolArguments: isStore ? undefined : call.arguments,
            fieldKey,
            funnelStep,
        });

        if (isStore) {
            this._storeVapiSessionData(parsedArgs, fieldKey, funnelStep);
        }

        if (isCloseElement) {
            try {
                view?._executeCloseElementCommand?.(parsedArgs || {});
            } catch (err) {
                console.warn('[VAPI] close element failed', err);
            }
        }

        if (
            executeEvents
            && !isStore
            && !isCloseElement
            && sourceId != null
            && allowed.has(call.name)
            && typeof view?._executeEventActions === 'function'
        ) {
            try {
                view._executeEventActions(sourceId, call.name);
            } catch (err) {
                console.warn('[VAPI] execute tool event failed', err);
            }
        }

        this._ackVapiToolCall(call);
    }

    async _finishAgentSceneChange(calls) {
        try {
            await this._summarizeForAgentSceneChange();
        } catch (err) {
            console.warn('[VAPI] scene change summarize failed', err);
        }
        const sourceId = this._getAgentId();
        const view = this.model?.view;
        if (sourceId == null || typeof view?._executeEventActions !== 'function') return;
        calls.forEach((call) => {
            try {
                view._executeEventActions(sourceId, call.name);
            } catch (err) {
                console.warn('[VAPI] execute tool event failed', err);
            }
        });
    }

    /** Map Vapi client tool-calls → Amplitude + Event-Action bindings for this chair. */
    _handleVapiToolCallsMessage(message) {
        const calls = this._extractVapiToolCalls(message);
        if (!calls.length) return;
        const sceneCalls = [];
        calls.forEach((call) => {
            if (this._toolCallStartsSceneChange(call)) sceneCalls.push(call);
            else this._runVapiToolCall(call);
        });
        if (!sceneCalls.length) return;
        sceneCalls.forEach((call) => this._runVapiToolCall(call, { executeEvents: false }));
        this._finishAgentSceneChange(sceneCalls);
    }

    /**
     * Unblock sync dashboard tools that still wait for a result.
     * Official client tools are fire-and-forget (`async: true`); this ack covers
     * copies defined on the assistant in the Vapi dashboard.
     */
    _ackVapiToolCall(call) {
        const toolCallId = call?.toolCallId;
        if (!toolCallId || !this._vapi || !this._vapiCallActive) return;
        const result = call.name === STORE_SESSION_DATA_TOOL_NAME
            ? 'stored'
            : (call.name === CLOSE_ELEMENT_TOOL_NAME ? 'closed' : 'ok');
        try {
            this._vapi.send({
                type: 'add-message',
                message: {
                    role: 'tool',
                    tool_call_id: toolCallId,
                    content: result,
                },
                triggerResponseEnabled: false,
            });
        } catch (err) {
            console.warn('[VAPI] tool result ack failed', err);
        }
    }

    _storeVapiSessionData(parsedArgs, fieldKey, funnelStep) {
        const value = String(parsedArgs?.value || parsedArgs?.summary || parsedArgs?.data || '').trim();
        if (!value) return;
        const amplitudeSessionId = getAmplitudeSessionId();
        saveSessionInputData(
            this.model,
            {
                objectId: this._getAgentId(),
                amplitudeSessionId,
                fieldKey: fieldKey || 'vapi_summary',
                value,
                source: 'vapi',
                funnelStep: funnelStep || fieldKey || 'qualified',
                validation: INPUT_VALIDATION.REQUIRED,
            },
            () => {
                this.model?.dispatchEvent?.({
                    type: 'session-data-stored',
                    data: {
                        objectId: this._getAgentId(),
                        objectName: this._getAgentDisplayName(),
                        fieldKey: fieldKey || 'vapi_summary',
                        funnelStep: funnelStep || fieldKey || 'qualified',
                        source: 'vapi',
                        amplitudeSessionId,
                    },
                });
            },
            (err) => {
                console.warn('[VAPI] storeSessionData failed', err);
            }
        );
    }

    _getAgentDisplayName() {
        const name = String(this.dbConfig?.chairName || this.dbConfig?.name || '').trim();
        return name || this._t('vrai-agent-text', 'AI Agent');
    }

    _notifyVapiUserError(message, state = 'connect-failed') {
        const msg = String(message || '').trim();
        if (!msg || VRVapiChairWithCSS3D._isMeetingEndedText(msg)) return;
        const statusEl = this.dom?.querySelector('.vrai-status-agent');
        const callControlsEl = this.dom?.querySelector('.vrai-agent-call-controls');
        const chairEl = this.dom?.querySelector('.vrai-agent-chair');
        if (callControlsEl) callControlsEl.classList.add('hidden');
        if (chairEl) chairEl.classList.remove('in-call');
        if (statusEl) {
            statusEl.textContent = msg;
            statusEl.className = 'status-side status-agent vrai-status-agent';
        }
        this._dispatchVapiEvent('vapi-agent-status', { state, message: msg });
        if (this.model?.alert) {
            this.model.alert('error', msg);
        }
    }

    _dispatchVapiEvent(type, extraDetail = {}) {
        const vapiAgentId = this._getAgentId();
        if (!vapiAgentId || !this.model || typeof this.model.dispatchEvent !== 'function') return;
        const vapiAgentName = this._getAgentDisplayName();
        this.model.dispatchEvent({ type, data: { vapiAgentId, vapiAgentName, ...extraDetail } });
    }

    dispatchCommand(commandText = null) {
        const text = String(commandText ?? this.dbConfig?.commandText ?? this.dbConfig?.action ?? '').trim();
        if (!text || !this._getAgentId()) return false;
        // Predefined commands must send immediately; never use promptTranscriptModal.
        if (this._vapi && this._vapiCallActive) {
            this._sendUserTranscriptMessage(text);
            return true;
        }
        return false;
    }

    _setupVapiClickHandler() {
        // Intentionally no generic click handler on the chair/image surface in view mode.
        // OrbitControls should work over the visible poster; only explicit overlay controls
        // start/end calls or run configured click actions.
    }

    _runConfiguredClickActionOrStartCall() {
        const sourceId = this._getAgentId();
        const view = this.model?.view;
        const events = view?._getClickEventsForSource?.(sourceId);
        if (events?.length) {
            events.forEach((ev) => view._executeAction(ev));
            return;
        }
        this._initVapiCall();
    }

    async _requestMedia() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: this._needsUserCamera(),
                audio: true,
            });
            this._webcamStream = stream;
            // New tracks start enabled; re-apply persisted media state.
            this.setMicrophoneMuted(this._isMicMuted);
            this.setCameraHidden(this._isCameraHidden);
            this._dispatchVapiEvent('vapi-agent-media', { stream });
            return true;
        } catch (e) {
            console.warn('[VAPI] getUserMedia failed', e);
            this._dispatchVapiEvent('vapi-agent-status', { state: 'media-failed', error: e });
            return false;
        }
    }

    _stopMedia() {
        if (this._webcamStream) {
            try {
                this._webcamStream.getTracks().forEach(t => t.stop());
            } catch (e) { }
            this._webcamStream = null;
        }
        this._isMicMuted = false;
        // Keep _isCameraHidden across calls so "camera off" stays until the user turns it on.
        this.getRelatedVapiCameras().forEach((camera) => camera.setCallCameraVisible?.(!this._isCameraHidden));
        this._dispatchVapiEvent('vapi-agent-media', { stream: null });
    }

    getRelatedVapiCameras() {
        const chairId = this._getAgentId();
        return (this.model?.view?.vraiAgentCameras || []).filter((camera) => {
            return String(camera?.dbConfig?.vapiAgentId) === String(chairId);
        });
    }

    /** Webcam is only shown on a linked user-camera object. Without one, audio is enough. */
    _needsUserCamera() {
        return this.getRelatedVapiCameras().length > 0;
    }

    _mediaRequestStatus() {
        if (this._needsUserCamera()) {
            return (typeof $trans !== 'undefined' && $trans('vrai-agent-request-media')) || 'Requesting camera & microphone...';
        }
        return (typeof $trans !== 'undefined' && $trans('vrai-agent-request-mic')) || 'Requesting microphone...';
    }

    _mediaFailedStatus() {
        if (this._needsUserCamera()) {
            return (typeof $trans !== 'undefined' && $trans('vrai-agent-media-failed')) || 'Camera/mic access required';
        }
        return (typeof $trans !== 'undefined' && $trans('vrai-agent-mic-failed')) || 'Microphone access required';
    }

    setMicrophoneMuted(muted) {
        this._isMicMuted = !!muted;
        // The websocket client mutes inside the PCM worklet and must keep the track
        // enabled. The Daily client captures its own mic, so the preview track can be disabled.
        if (this._vapiTransport !== 'websocket') {
            this._webcamStream?.getAudioTracks?.().forEach((track) => {
                track.enabled = !this._isMicMuted;
            });
        }

        if (this._vapi && this._vapiCallActive) {
            try {
                if (typeof this._vapi.setMuted === 'function') {
                    this._vapi.setMuted(this._isMicMuted);
                } else if (typeof this._vapi.setMicMuted === 'function') {
                    this._vapi.setMicMuted(this._isMicMuted);
                } else if (this._isMicMuted && typeof this._vapi.mute === 'function') {
                    this._vapi.mute();
                } else if (!this._isMicMuted && typeof this._vapi.unmute === 'function') {
                    this._vapi.unmute();
                }
            } catch (e) {
                console.warn('[VAPI] mic mute sync failed', e);
            }
        }
    }

    setCameraHidden(hidden) {
        this._isCameraHidden = !!hidden;
        this._webcamStream?.getVideoTracks?.().forEach((track) => {
            track.enabled = !this._isCameraHidden;
        });
        this.getRelatedVapiCameras().forEach((camera) => camera.setCallCameraVisible?.(!this._isCameraHidden));
    }

    _stopAllVapiCalls() {
        const chairs = this.model?.view?.vraiAgentChairs || [];
        chairs.forEach(ch => {
            if (!ch || ch === this) return;
            if (typeof ch.hasActiveVapiSession === 'function' && !ch.hasActiveVapiSession()) return;
            ch?._forceStopVapiSession?.({
                callEndData: { cancelled: true, switchedAgent: true }
            });
        });
    }

    async _initVapiCall() {
        if (this._vapiCallActive || this._vapiConnecting || this._vapiConsentPending) return;

        this._vapiConsentPending = true;
        let allowed = false;
        try {
            allowed = await requestAiInteractionConsent(this.model);
        } finally {
            this._vapiConsentPending = false;
        }
        if (!allowed) return;
        if (this._vapiCallActive || this._vapiConnecting) return;

        const sessionVersion = this._vapiSessionVersion + 1;
        this._vapiSessionVersion = sessionVersion;
        this._vapiConnecting = true;
        this._updateCallRunningClass();
        this._dispatchVapiEvent('vapi-agent-status', { state: 'connecting' });
        this._clearCallReportRetry();
        if (!this._isCurrentVapiSession(sessionVersion) || !this._vapiConnecting) return;
        const isSquad = !!(this.dbConfig?.isSquad);
        // For squads, VAPI_SQUAD_ID is the call target; for agents, VAPI_ASSISTANT_ID
        const assistantId = isSquad
            ? (this.dbConfig?.VAPI_SQUAD_ID || this.dbConfig?.VAPI_ASSISTANT_ID || '').trim()
            : (this.dbConfig?.VAPI_ASSISTANT_ID || '').trim();
        if (!assistantId) {
            this._vapiConnecting = false;
            this._updateCallRunningClass();
            this._notifyVapiUserError(
                (typeof $trans !== 'undefined' && $trans('vrai-agent-no-assistant')) || 'Agent not configured',
                'no-assistant'
            );
            return;
        }
        const worldId = ((this.model?.config?.worldId ?? '') + '').trim();
        const sceneId = ((this.model?.currentScene?.id ?? this.model?.currentSceneId ?? '') + '').trim();
        const base = (this.model?.config?.baseWebUrl || (typeof window !== 'undefined' && window.location?.origin) || '').toString().replace(/\/$/, '');
        const tokenUrl = base ? `${base}/vapi-agent/token` : '/vapi-agent/token';

        const otherChairOnCall = (this.model?.view?.vraiAgentChairs || []).find(
            ch => ch !== this && (ch._vapiCallActive || ch._vapiConnecting)
        );
        const switchConfirmMsg = (typeof $trans !== 'undefined' && $trans('vrai-agent-switch-call-confirm')) || 'Another agent is on a call. End that call and start this one?';
        const doConnect = async () => {
            this._dispatchVapiEvent('vapi-agent-status', { state: 'request-media' });
            const ok = await this._requestMedia();
            if (!ok) {
                if (!this._isCurrentVapiSession(sessionVersion)) return;
                this._vapiConnecting = false;
                this._updateCallRunningClass();
                this._dispatchVapiEvent('vapi-agent-call-end', { cancelled: true });
                return;
            }
            if (!this._isCurrentVapiSession(sessionVersion) || !this._vapiConnecting) {
                return;
            }
            this._stopAllVapiCalls();

            if (!this._isCurrentVapiSession(sessionVersion) || !this._vapiConnecting) {
                return;
            }

            this._dispatchVapiEvent('vapi-agent-status', { state: 'connecting' });
            this._aiTokenCallRecorded = false;
            this._clearStaleSessionConversation();

            try {
                const body = {
                    worldId,
                    sceneId,
                    agentId: this._getAgentId(),
                    sessionString: this._getVisitSessionString(),
                };
                const overrideLibraryId = this.dbConfig?.overrideLibraryId;
                if (overrideLibraryId) body.overrideLibraryId = overrideLibraryId;
                const tokenPromise = fetch(tokenUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                }).then((res) => res.json());
                const aiTokenPromise = this._loadAiTokenPrompt();
                const data = await tokenPromise;
                await aiTokenPromise;
                if (!this._isCurrentVapiSession(sessionVersion) || !this._vapiConnecting) {
                    this._stopMedia();
                    return;
                }
                if (!data.token || !data.proxyUrl) {
                    this._vapiConnecting = false;
                    this._updateCallRunningClass();
                    const msg = (data?.error) || ((typeof $trans !== 'undefined' && $trans('vrai-agent-connect-failed')) || 'Failed to connect');
                    this._notifyVapiUserError(msg, 'connect-failed');
                    this._stopMedia();
                    return;
                }
                this._vapiLocalId = this._newCallLocalId();
                this._vapiCallId = null;
                this._vapiConversationSnapshot = null;
                this._vapiReportToken = data.reportToken || null;
                this._vapiSaveTranscriptUrl = data.saveTranscriptUrl || this._resolveSaveTranscriptUrl();
                this._vapiWebhookUrl = data.webhookUrl || null;
                const memoryPending = !!this._sessionMemoryPending;
                await this._waitForPendingSummarize();
                if (!this._isCurrentVapiSession(sessionVersion) || !this._vapiConnecting) {
                    this._stopMedia();
                    return;
                }
                let serverConversations = Array.isArray(data.sessionConversations) ? data.sessionConversations : [];
                if (memoryPending) {
                    try {
                        const fresh = await this._fetchSessionConversations(tokenUrl, body);
                        if (Array.isArray(fresh)) serverConversations = fresh;
                    } catch (err) {
                        console.warn('[VAPI] reload session conversations failed', err);
                    }
                    this._sessionMemoryPending = false;
                }
                if (serverConversations.length) {
                    this._rememberServerConversations(serverConversations);
                }
                this._applySummaryHandoff();
                this._loggedSessionPromptForCall = false;
                if (!this._vapiReportToken) {
                    console.warn('[VAPI] /vapi-agent/token did not return reportToken; hang-up will not save');
                }
                const usePublicKeyCall = data.callTransport === 'web';
                this._vapiTransport = usePublicKeyCall ? 'web' : 'websocket';
                // Daily allows one call object. The page preloads one, and leaving it
                // up during the fast call makes Daily eject with VM2600.
                await releasePreloadedVapiWebRtc();
                const vapi = usePublicKeyCall
                    ? new Vapi(data.token, data.proxyUrl)
                    : createVapiRCall();
                this._vapi = vapi;
                vapi.on('call-start', () => {
                    if (this._isStaleVapiInstance(vapi, sessionVersion)) {
                        this._stopDetachedVapiInstance(vapi);
                        return;
                    }
                    this._vapiCallActive = true;
                    this._vapiConnecting = false;
                    this._updateCallRunningClass();
                    this._injectVapiCallContext(vapi);
                    if (!this._isEditMode && this.state?.mode !== 'edit') {
                        this._recordAiTokenCall();
                    }
                    this._dispatchVapiEvent('vapi-agent-call-start', { listening: !!usePublicKeyCall });
                });
                vapi.on('speech-start', () => {
                    if (this._isStaleVapiInstance(vapi, sessionVersion)) return;
                    this._vapiAssistantSpeaking = true;
                    this._dispatchVapiEvent('vapi-agent-status', { state: 'agent-talking' });
                });
                vapi.on('speech-end', () => {
                    if (this._isStaleVapiInstance(vapi, sessionVersion)) return;
                    if (!this._vapiAssistantSpeaking) return;
                    this._vapiAssistantSpeaking = false;
                    this._dispatchVapiEvent('vapi-agent-status', { state: 'agent' });
                });
                vapi.on('call-start-success', (e) => {
                    if (this._isStaleVapiInstance(vapi, sessionVersion)) return;
                    this._captureCallIdFromPayload(e);
                    if (e?.callId && e.callId !== 'unknown') this._vapiCallId = e.callId;
                });
                vapi.on('call-start-progress', (e) => {
                    if (this._isStaleVapiInstance(vapi, sessionVersion)) return;
                    this._captureCallIdFromPayload(e);
                    const id = e?.metadata?.callId || e?.callId;
                    if (id && id !== 'unknown') this._vapiCallId = id;
                });
                vapi.on('call-end', () => {
                    if (this._isStaleVapiInstance(vapi, sessionVersion)) {
                        this._stopDetachedVapiInstance(vapi);
                        return;
                    }
                    this._forceStopVapiSession({
                        callEndData: { normal: true },
                        stopSdk: false
                    });
                });
                vapi.on('call-start-failed', (err) => {
                    if (this._isStaleVapiInstance(vapi, sessionVersion)) {
                        this._stopDetachedVapiInstance(vapi);
                        return;
                    }
                    const msg = VRVapiChairWithCSS3D.formatVapiError(err)
                        || ((typeof $trans !== 'undefined' && $trans('vrai-agent-connect-failed')) || 'Connection failed');
                    this._notifyVapiUserError(msg, 'connect-failed');
                    this._forceStopVapiSession({
                        dispatchCallEnd: false,
                        callEndData: { failed: true },
                        stopSdk: false
                    });
                });
                vapi.on('error', (err) => {
                    if (this._isStaleVapiInstance(vapi, sessionVersion)) {
                        this._stopDetachedVapiInstance(vapi);
                        return;
                    }
                    if (VRVapiChairWithCSS3D.isNormalCallEnd(err)) {
                        this._forceStopVapiSession({
                            callEndData: { normal: true },
                            stopSdk: false
                        });
                        return;
                    }
                    const msg = VRVapiChairWithCSS3D.formatVapiError(err)
                        || ((typeof $trans !== 'undefined' && $trans('vrai-agent-connect-failed')) || 'Connection failed');
                    this._notifyVapiUserError(msg, 'connect-failed');
                    this._forceStopVapiSession({
                        dispatchCallEnd: false,
                        callEndData: { failed: true, errored: true },
                        stopSdk: false
                    });
                    this._dispatchVapiEvent('vapi-agent-error', { error: err });
                });
                vapi.on('message', (message) => {
                    if (this._isStaleVapiInstance(vapi, sessionVersion)) {
                        this._stopDetachedVapiInstance(vapi);
                        return;
                    }
                    this._captureCallIdFromPayload(message);
                    if (message?.type === 'conversation-update') {
                        this._storeConversationSnapshot(message);
                    }
                    if (message?.type === 'status-update' && message.status === 'in-progress' && !this._vapiAssistantSpeaking) {
                        this._dispatchVapiEvent('vapi-agent-status', { state: 'agent' });
                    }
                    if (message?.type === 'tool-calls' || message?.type === 'function-call') {
                        if (!this._vapiAssistantSpeaking) {
                            this._dispatchVapiEvent('vapi-agent-status', { state: 'agent-thinking' });
                        }
                        this._handleVapiToolCallsMessage(message);
                        return;
                    }
                    if (message?.type === 'transcript' && message.transcript) {
                        const isFinal = message.transcriptType === 'final';
                        const role = message.role || 'assistant';
                        this._addCallTranscript(message.role, message.transcript, isFinal, {
                            sentAt: message.sentAt,
                            source: message.source,
                            forceNewEntry: message.source === 'command'
                        });
                        if (!isFinal) {
                            if (role === 'assistant') {
                                this._vapiAssistantSpeaking = true;
                                this._dispatchVapiEvent('vapi-agent-status', { state: 'agent-talking' });
                            }
                            this._dispatchVapiEvent('vapi-agent-transcript', {
                                role: message.role,
                                transcript: message.transcript,
                                isFinal: false,
                                sentAt: message.sentAt,
                                source: message.source
                            });
                            return;
                        }
                        this._dispatchVapiEvent('vapi-agent-transcript', {
                            role: message.role,
                            transcript: message.transcript,
                            isFinal: true,
                            sentAt: message.sentAt,
                            source: message.source
                        });
                        if (role === 'user') {
                            this._dispatchVapiEvent('vapi-agent-status', { state: 'agent-thinking' });
                        }
                    }
                });
                const aiEventOverrides = this._buildAiEventAssistantOverrides();
                const reportOverrides = this._buildCallReportOverrides();
                if (isSquad) {
                    const squadId = data.assistantId || assistantId;
                    const customVars = this.dbConfig?.squadCustomVariables || undefined;
                    const squadOverrides = mergeVapiAssistantOverrides(
                        mergeVapiAssistantOverrides(
                            (customVars && typeof customVars === 'object') ? customVars : null,
                            aiEventOverrides
                        ),
                        reportOverrides
                    );
                    if (usePublicKeyCall) {
                        await vapi.start(undefined, squadOverrides || undefined, squadId);
                    } else {
                        await vapi.start({
                            proxyUrl: data.proxyUrl,
                            token: data.token,
                            squadId,
                            squadOverrides: squadOverrides || undefined,
                            mediaStream: this._webcamStream,
                        });
                    }
                } else {
                    const assistantOverrides = mergeVapiAssistantOverrides(aiEventOverrides, reportOverrides) || undefined;
                    if (usePublicKeyCall) {
                        await vapi.start(data.assistantId || assistantId, assistantOverrides);
                    } else {
                        await vapi.start({
                            proxyUrl: data.proxyUrl,
                            token: data.token,
                            assistantId: data.assistantId || assistantId,
                            assistantOverrides,
                            mediaStream: this._webcamStream,
                        });
                    }
                }
            } catch (err) {
                if (!this._isCurrentVapiSession(sessionVersion)) {
                    this._stopMedia();
                    return;
                }
                console.warn('[VAPI] token/vapi init failed', err);
                this._vapiConnecting = false;
                this._updateCallRunningClass();
                this._stopMedia();
                const msg = VRVapiChairWithCSS3D.formatVapiError(err)
                    || ((typeof $trans !== 'undefined' && $trans('vrai-agent-connect-failed')) || 'Connection failed');
                this._notifyVapiUserError(msg, 'connect-failed');
            }
        };

        if (otherChairOnCall && this.model?.confirm) {
            this.model.confirm(switchConfirmMsg, () => { doConnect(); }, {
                onCancel: () => {
                    if (!this._isCurrentVapiSession(sessionVersion)) return;
                    this._vapiConnecting = false;
                    this._updateCallRunningClass();
                    this._dispatchVapiEvent('vapi-agent-call-end', { cancelled: true });
                }
            });
            return;
        }
        await doConnect();
    }

    showObjectMenu() {
        const txt = (typeof $trans !== 'undefined' && $trans('vrai-agent-text')) ? $trans('vrai-agent-text') : 'VRAI Agent';
        $('.object-menu .object-name').text(txt);
        $('.object-menu .object-name').attr('data-lang', 'vrai-agent');
        $('.object-menu').addClass('is-active');
        $('.object-menu').addClass('edit');
        $('.vrai-agent-settings').addClass('is-active');
        $('.perspective-settings:not(.vrai-agent-perspective), .tooltip-settings, .video-loop, .iframe-agent-settings').addClass('hidden');
        $('#vrai_show_agent_settings').addClass('hidden');
    }

    toggleSelect() {
        this.dom?.querySelector('.vrai-agent-chair')?.classList?.toggle('active');
    }

    resetPerspective() {
        this._resetPerspectiveToRect();
    }

    _ensureVapiCues() {
        if (this._vapiCues) return this._vapiCues;
        const cue = (src, loop) => {
            const audio = new Audio(src);
            audio.preload = 'auto';
            audio.loop = loop;
            audio.volume = 0.5;
            return audio;
        };
        this._vapiCues = {
            loop: cue('media/agent-loop.mp3', true),
            connected: cue('media/call-connected.mp3', false),
            ended: cue('media/call-ended.mp3', false),
        };
        return this._vapiCues;
    }

    _haltVapiCue(audio) {
        if (!audio) return;
        try {
            audio.pause();
            audio.currentTime = 0;
        } catch (e) { /* ignore */ }
    }

    _playVapiCue(audio) {
        if (!audio) return;
        try {
            audio.currentTime = 0;
            const pending = audio.play();
            if (pending && typeof pending.catch === 'function') pending.catch(() => {});
        } catch (e) { /* autoplay blocked */ }
    }

    _startVapiWaitingCue() {
        if (this._vapiCueState === 'waiting' || this._vapiCueState === 'connected') return;
        const cues = this._ensureVapiCues();
        this._haltVapiCue(cues.connected);
        this._haltVapiCue(cues.ended);
        this._vapiCueState = 'waiting';
        this._playVapiCue(cues.loop);
    }

    _playVapiConnectedCue() {
        if (this._vapiCueState === 'connected') return;
        const cues = this._ensureVapiCues();
        this._haltVapiCue(cues.loop);
        this._haltVapiCue(cues.ended);
        this._vapiCueState = 'connected';
        this._playVapiCue(cues.connected);
    }

    _playVapiEndedCue() {
        if (this._vapiCueState !== 'waiting' && this._vapiCueState !== 'connected') return;
        const cues = this._ensureVapiCues();
        this._haltVapiCue(cues.loop);
        this._haltVapiCue(cues.connected);
        this._vapiCueState = 'ended';
        this._playVapiCue(cues.ended);
    }

    destroy() {
        this._haltVapiCue(this._vapiCues?.loop);
        if (this._vapiCueState !== 'ended') {
            this._haltVapiCue(this._vapiCues?.connected);
            this._haltVapiCue(this._vapiCues?.ended);
        }
        this._vapiCueState = 'idle';
        try {
            this._detachDragListeners();
        } catch (e) { /* no-op */ }
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        if (this.hasActiveVapiSession()) {
            this._persistCallReport({ cancelled: true }, { keepalive: true });
            this._forceStopVapiSession({ dispatchCallEnd: false });
        }
        this._clearCallReportRetry();
        if (typeof window !== 'undefined' && this._onPageHidePersistCallReport) {
            window.removeEventListener('pagehide', this._onPageHidePersistCallReport);
        }
        this._updateCallRunningClass();
        if (this._timerInterval) {
            clearInterval(this._timerInterval);
            this._timerInterval = null;
        }
        if (this.model && this._handleCallStart && this._handleCallEnd) {
            this.model.removeEventListener('vapi-agent-call-start', this._handleCallStart);
            this.model.removeEventListener('vapi-agent-call-end', this._handleCallEnd);
        }
        if (this.model && this._handleStatus) {
            this.model.removeEventListener('vapi-agent-status', this._handleStatus);
        }
        if (this.model && this._handleVapiError) {
            this.model.removeEventListener('vapi-agent-error', this._handleVapiError);
        }
        if (this.model && this._handleSendText) {
            this.model.removeEventListener('vapi-agent-send-text', this._handleSendText);
        }
        if (this.model && this._handleEndVapiCallRequest) {
            this.model.removeEventListener(SCENE_INTERACTION.END_VAPI_CALL, this._handleEndVapiCallRequest);
        }
        this._chairIndicatorEl?.removeEventListener('pointerdown', this._onChairOverlayPointerDown);
        this._chairIndicatorEl?.removeEventListener('click', this._onChairStartClick);
        this._chairIndicatorEl?.removeEventListener('pointerenter', this._handleFloatingOverlayPointerEnter);
        this._chairIndicatorEl?.removeEventListener('pointerleave', this._handleFloatingOverlayPointerLeave);
        this._chairEndIndicatorEl?.removeEventListener('pointerdown', this._onChairOverlayPointerDown);
        this._chairEndIndicatorEl?.removeEventListener('click', this._onChairEndClick);
        this._chairEndIndicatorEl?.removeEventListener('pointerenter', this._handleFloatingOverlayPointerEnter);
        this._chairEndIndicatorEl?.removeEventListener('pointerleave', this._handleFloatingOverlayPointerLeave);
        this._chairIndicatorEl?.remove?.();
        this._chairEndIndicatorEl?.remove?.();
        if (this.dom?.parentNode) this.dom.parentNode.removeChild(this.dom);
    }
}

ComponentRegistry.register({
    type: 'vraiAgent',
    displayNameKey: 'vrai-agent-text',
    getDisplayName: (obj) => `AI-AGENT-${obj.chairName || obj.id || 'Agent'}`,
    events: [
        { value: 'click', labelKey: 'event-click' },
        { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
        { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
        { value: 'callStarted', labelKey: 'event-call-started' },
        { value: 'callEnded', labelKey: 'event-call-ended' },
        { value: 'agentChanged', labelKey: 'event-agent-changed' },
    ],
    actions: [
        { value: 'sendCommand', labelKey: 'action-send-command' },
        { value: 'startCall', labelKey: 'action-start-call' },
        { value: 'endCall', labelKey: 'action-end-call' },
        { value: 'switchAgent', labelKey: 'action-switch-agent' },
        { value: 'openInputField', labelKey: 'action-open-input-field' },
    ],
});

export { VRVapiChairWithCSS3D };
